import * as React from "react";
import { useEffect, useState, useRef } from "react";
import CustomDataLayerUpload from "./CustomDataLayerUpload";
// Modern inline SVG nav icons (stroke-based, Lucide style)

import { API_BASE, apiFetch } from "../../api/http";
import { getCourtLinks } from "../../features/courtLinks/api";
import { getTemplate } from "../../features/templates/api";
import { postFlawAnalysis } from "../../features/flawAnalysis/api";
import { detectDocTypeFromContent, resolveDocumentType, DocumentType } from "../../features/documents/documentType";
import { postInjectionScan, InjectionFinding } from "../../features/security/api";
import { excludeFindings, firstSearchableLine, hashText } from "../../features/security/guard";
import { getClientChecklist } from "../../features/clientChecklist/api";
import { postJurisdictionCheck, postTalkingPoints, JurisdictionIssue } from "../../features/rag/api";
import { postClauseCheck, postClauseCheckRefine, postClauseCheckRefineBatch, ClauseIssue } from "../../features/clauseCheck/api";
import { postScorecard } from "../../features/analysis/api";

/*
* Word-level diff using LCS (longest common subsequence).
* Wraps *every* word in `newStr` that is NOT part of the LCS with `oldStr` in <b>…</b>.
*/
function boldWordDiff(oldStr: string, newStr: string): string {
  const oldWords = oldStr.split(/\s+/);
  const newWords = newStr.split(/\s+/);
  const m = oldWords.length, n = newWords.length;
  // Build LCS table
  const dp: number[][] = Array(m + 1)
    .fill(0)
    .map(() => Array(n + 1).fill(0));
  for (let i = m - 1; i >= 0; i--) {
    for (let j = n - 1; j >= 0; j--) {
      if (oldWords[i] === newWords[j]) {
        dp[i][j] = dp[i + 1][j + 1] + 1;
      } else {
        dp[i][j] = Math.max(dp[i + 1][j], dp[i][j + 1]);
      }
    }
  }
  // Backtrack to emit diff
  let i = 0, j = 0;
  const out: string[] = [];
  while (i < m && j < n) {
    if (oldWords[i] === newWords[j]) {
      out.push(newWords[j]);
      i++; j++;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      // skip oldWords[i]
      i++;
    } else {
      // newWords[j] is an insertion → bold it and paint darkgreen
      out.push(`<b style='color:darkgreen'>${newWords[j++]}</b>`);
    }
  }
  // Any trailing insertions → bold + darkgreen
  while (j < n) {
    out.push(`<b style='color:darkgreen'>${newWords[j++]}</b>`);
  }
  return out.join(" ");
}

// Helper function to strip formatting from Recommended Edits content
function stripRecommendedEditsFormatting(html: string): string {
  // Find the Recommended Edits section and strip all formatting tags and inline styles
  return html.replace(
    /(<strong>(?:Recommended Edits|Correction):<\/strong>\s*")([^"]*)(")/gi,
    (_match, prefix, content, suffix) => {
      // Strip all HTML tags and inline styles from the content
      const plainText = content
        .replace(/<b[^>]*>/gi, '')
        .replace(/<\/b>/gi, '')
        .replace(/<em[^>]*>/gi, '')
        .replace(/<\/em>/gi, '')
        .replace(/<i[^>]*>/gi, '')
        .replace(/<\/i>/gi, '')
        .replace(/<strong[^>]*>/gi, '')
        .replace(/<\/strong>/gi, '')
        .replace(/style="[^"]*"/gi, '')
        .replace(/style='[^']*'/gi, '');
      return `${prefix}${plainText}${suffix}`;
    }
  );
}

type DocStatus = "blank" | "existing" | null;
type DocType = "protocol" | "protective" | null;
type FlowStep =
  | "initial"
  | "clientChecklist"
  | "analyzing"
  | "chooseType"
  | "confirmExisting"
  | "jurisdictionAuto"
  | "jurisdictionManual"
  | "nextSteps"
  | "analysisComplete"
  | "clauseCheck"
  | "esiFlowEditor"
  | "talkingPoints"
  | "closed";

type EditorFlaw = {
  bullet: string;
  correction: string;
  sectionNumber: number;
  source: "jurisdiction" | "clause";
};

type AcceptedCorrection = {
  sectionNumber: number;
  heading: string;
  details: string;
};

type CorrectionSummaryProps = {
  heading: string;
  details: string;
};

// ====== AI Scorecard Types ======
type AnchorStatus = "pass" | "warn" | "fail";
type SectionBulletsProp = {
  custodianCap: AnchorStatus; temporalCutoff: AnchorStatus; excludesNAS: AnchorStatus; searchMethod: AnchorStatus;
};
type SectionBulletsCost = {
  globalDedupe: AnchorStatus; TAR: AnchorStatus; metadataOverbroad: AnchorStatus; costShift: AnchorStatus;
};
type SectionBulletsPriv = {
  rule502d: AnchorStatus; clawbackVague: AnchorStatus; confTiers: AnchorStatus; inadvertent: AnchorStatus;
};
type SectionBulletsGov = {
  crossBorder: AnchorStatus; byod: AnchorStatus; sensitive: AnchorStatus; retention: AnchorStatus;
};
type SectionBulletsDisp = {
  meetConfer: AnchorStatus; escalation: AnchorStatus; disputeTiming: AnchorStatus; specialMaster: AnchorStatus;
};
// Protective Order-specific bullets
type SectionBulletsConf = {
  multiTier: AnchorStatus; inHouseScope: AnchorStatus; depoFiling: AnchorStatus; litigationOnly: AnchorStatus;
};
type SectionBulletsUse = {
  litOnly: AnchorStatus; expertVetting: AnchorStatus; security: AnchorStatus; byod: AnchorStatus;
};
type AnyBullets = SectionBulletsProp | SectionBulletsCost | SectionBulletsPriv | SectionBulletsGov | SectionBulletsDisp | SectionBulletsConf | SectionBulletsUse;
type Section = {
  id: "prop" | "cost" | "priv" | "gov" | "disp" | "conf" | "use";
  title: string; score: number; emoji: string;
  bullets: AnyBullets; recommendation: string;
};
type Scorecard = {
  matter: string; date: string; generatedBy: string;
  composite: number; compositeLabel: "Low" | "Moderate" | "High"; compositeEmoji: string;
  summary: string; sections: Section[]; keyRisks: string[];
};

// API_BASE moved to shared api/http

// directly below your imports
const NavSvg: React.FC<{ color: string; children: React.ReactNode }> = ({ color, children }) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">{children}</svg>
);

const navItems: {
  key: FlowStep;
  label: string;
  icon: (color: string) => React.ReactNode;
}[] = [
    {
      key: "clientChecklist",
      label: "Client Checklist",
      icon: (c) => (
        <NavSvg color={c}>
          <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2" />
          <rect x="9" y="3" width="6" height="4" rx="1" />
          <path d="M9 14l2 2 4-4" />
        </NavSvg>
      ),
    },
    {
      key: "analyzing",
      label: "Analyzing",
      icon: (c) => (
        <NavSvg color={c}>
          <circle cx="11" cy="11" r="8" />
          <path d="M21 21l-4.35-4.35" />
          <path d="M8 11h2v3h2V9h2v5" />
        </NavSvg>
      ),
    },
    {
      key: "analysisComplete",
      label: "Jurisdiction Check",
      icon: (c) => (
        <NavSvg color={c}>
          <path d="M12 3v18" />
          <path d="M3 7h18" />
          <path d="M7 7L4 13c0 1.5 1.34 2 3 2s3-.5 3-2L7 7z" />
          <path d="M17 7l-3 6c0 1.5 1.34 2 3 2s3-.5 3-2l-3-6z" />
          <path d="M9 21h6" />
        </NavSvg>
      ),
    },
    {
      key: "clauseCheck",
      label: "Clause Check",
      icon: (c) => (
        <NavSvg color={c}>
          <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z" />
          <path d="M14 2v6h6" />
          <path d="M9 15l2 2 4-4" />
        </NavSvg>
      ),
    },
    {
      key: "esiFlowEditor",
      label: "ESI Flow Editor",
      icon: (c) => (
        <NavSvg color={c}>
          <path d="M12 20h9" />
          <path d="M16.5 3.5a2.12 2.12 0 013 3L7 19l-4 1 1-4L16.5 3.5z" />
        </NavSvg>
      ),
    },
    {
      key: "talkingPoints",
      label: "Talking Points",
      icon: (c) => (
        <NavSvg color={c}>
          <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
          <path d="M8 9h8" />
          <path d="M8 13h5" />
        </NavSvg>
      ),
    },
  ];

// ====== Reusable UI Patterns ======

// --- Spinner: replaces 8+ duplicated SVG spinner blocks ---
const Spinner: React.FC<{ size?: "sm" | "md" | "lg"; label?: string }> = ({ size = "md", label }) => {
  const sizeClasses = { sm: "h-4 w-4", md: "h-5 w-5", lg: "h-8 w-8" };
  return (
    <div className="flex items-center">
      <svg
        className={`animate-spin ${sizeClasses[size]} mr-3 text-brand-500`}
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
      >
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
      </svg>
      {label && <span className="text-gray-600 text-sm">{label}</span>}
    </div>
  );
};

// --- Button class constants ---
const BTN_PRIMARY    = "bg-brand-500 hover:bg-brand-600 text-white font-medium text-sm px-4 py-2 rounded-md transition-colors duration-150 shadow-sm";
const BTN_PRIMARY_SM = "bg-brand-500 hover:bg-brand-600 text-white font-medium text-xs px-3 py-1.5 rounded-md transition-colors duration-150 shadow-sm";
const BTN_PRIMARY_LG = "bg-brand-500 hover:bg-brand-600 text-white font-medium text-base px-6 py-3 rounded-md transition-colors duration-150 shadow-sm";
const BTN_GHOST      = "bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium text-xs px-3 py-1.5 rounded-md transition-colors duration-150";
const BTN_SECONDARY  = "bg-white hover:bg-gray-50 text-brand-700 font-medium text-sm px-4 py-2 rounded-md border border-brand-300 transition-colors duration-150";
const BTN_SECONDARY_SM = "bg-white hover:bg-gray-50 text-brand-700 font-medium text-xs px-3 py-1.5 rounded-md border border-brand-300 transition-colors duration-150";

// --- NavBadge: replaces 6 duplicated inline-style badge spans ---
const NavBadge: React.FC<{ count: number; variant: "warning" | "success" }> = ({ count, variant }) => (
  <span className={`absolute -top-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold text-white leading-none ${
    variant === "warning" ? "bg-salmon" : "bg-brand-500"
  }`}>
    {variant === "success" ? "\u2713" : count}
  </span>
);

// --- IssueCard: unifies Jurisdiction, Clause, and Editor issue cards ---
const IssueCard: React.FC<{
  index: number;
  heading: string;
  isExpanded: boolean;
  onToggle: () => void;
  content: string;
  onAccept: () => void;
  onIgnore: () => void;
  acceptLabel?: string;
  ignoreLabel?: string;
}> = ({ index, heading, isExpanded, onToggle, content, onAccept, onIgnore, acceptLabel = "Accept", ignoreLabel = "Ignore" }) => (
  <div className="mb-3 rounded-lg border border-gray-200 bg-white shadow-sm overflow-hidden">
    <div
      className="flex items-center cursor-pointer px-4 py-3 hover:bg-gray-50 transition-colors"
      onClick={onToggle}
    >
      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-gold flex items-center justify-center text-xs font-bold text-white mr-3">
        {index + 1}
      </span>
      <span className="font-semibold text-gray-900 text-sm leading-snug flex-1">{heading}</span>
      <span className="ml-auto text-gray-400 text-xs">{isExpanded ? "\u25BC" : "\u25B6"}</span>
    </div>
    {isExpanded && (
      <div className="border-t border-gray-100 px-4 py-3">
        {content ? (
          <div
            className="text-sm text-gray-800 leading-relaxed mb-3 [&_b]:!text-gray-900 [&_b]:!font-normal [&_em]:!font-normal [&_em]:!not-italic [&_i]:!font-normal [&_i]:!not-italic"
            style={{ whiteSpace: "pre-wrap" }}
            dangerouslySetInnerHTML={{ __html: content }}
          />
        ) : (
          <Spinner label="Analyzing draft, please wait..." />
        )}
        <div className="flex gap-3 mt-3 pt-3 border-t border-gray-100">
          <button onClick={onAccept} className={`${BTN_PRIMARY_SM}`}>
            {acceptLabel}
          </button>
          <button onClick={onIgnore} className={`${BTN_GHOST}`}>
            {ignoreLabel}
          </button>
        </div>
      </div>
    )}
  </div>
);

const CorrectionSummary: React.FC<CorrectionSummaryProps> = ({ heading, details }) => {
  const [summary, setSummary] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const summarize = async () => {
      const prompt = `You are an expert legal assistant. Given the following accepted correction details, generate two concise bullet points that clearly state what changes were made:\n\n"${details}"\n\nBullet points:`;
      try {
        // Get clientName from localStorage for this call
        const clientName = localStorage.getItem('clientName') || '';
        const { analysis } = await postFlawAnalysis({ prompt, clientName, documentType: "protocol" });
        const summarizedText = analysis.trim();
        setSummary(summarizedText);
      } catch (error) {
        console.error("Error summarizing correction:", error);
        setSummary(details); // fallback
      }
      setLoading(false);
    };
    summarize();
  }, [details]);

  return (
    <div style={{ marginBottom: "1rem" }}>
      <p>
        <strong>{heading}</strong>:
      </p>
      {loading ? (
        <p>Summarizing changes...</p>
      ) : (
        <div style={{ whiteSpace: "pre-wrap" }}>{summary}</div>
      )}
    </div>
  );
};

// Every district the Jurisdiction Check supports: display name -> code used in
// the backend requirements data. ONE table feeds the dropdown, text detection
// and every API call — separate hand-maintained copies drifted before (CDCA
// was missing from some). Keep in sync with ESIProBackend/src/features/rag/routes.js.
const JURISDICTION_CODES: Record<string, string> = {
  "Northern District of California": "NDCA",
  "Southern District of California": "SDCA",
  "Central District of California": "CDCA",
  "Western District of Washington": "WDWA",
  "District of New Hampshire": "DNH",
  "District of Arizona": "AZD",
  "District of Connecticut": "CTD",
  "District of Delaware": "DED",
  "District of Hawaii": "HID",
  "District of Idaho": "IDD",
  "District of Maine": "MED",
  "District of Maryland": "MDD",
  "District of Montana": "MTD",
  "District of Nevada": "NVD",
  "District of New Jersey": "NJD",
  "District of Puerto Rico": "PRD",
  "Eastern District of California": "EDCA",
  "Eastern District of Louisiana": "EDLA",
  "Eastern District of New York": "EDNY",
  "Eastern District of Texas": "EDTX",
  "Eastern District of Virginia": "EDVA",
  "Eastern District of Washington": "EDWA",
  "Middle District of Alabama": "MDAL",
  "Middle District of Florida": "MDFL",
  "Middle District of Georgia": "MDGA",
  "Middle District of North Carolina": "MDNC",
  "Middle District of Pennsylvania": "MDPA",
  "Northern District of Florida": "NDFL",
  "Northern District of Illinois": "NDIL",
  "Northern District of New York": "NDNY",
  "Northern District of Texas": "NDTX",
  "Northern District of West Virginia": "NDWV",
  "Northern/Southern District of Mississippi": "MSD",
  "Southern District of Alabama": "SDAL",
  "Southern District of Florida": "SDFL",
  "Southern District of New York": "SDNY",
  "Southern District of Texas": "SDTX",
  "Southern District of West Virginia": "SDWV",
  "Western District of New York": "WDNY",
  "Western District of North Carolina": "WDNC",
  "Western District of Pennsylvania": "WDPA",
};

// Districts that have model templates for the blank-document flow. Keep in sync
// with JURISDICTION_FILE_MAPPINGS in ESIProBackend/src/config/constants.js.
const TEMPLATE_JURISDICTIONS = [
  "Northern District of California",
  "Southern District of California",
  "Western District of Washington",
  "District of New Hampshire",
];

// Other phrasings that identify a supported district in document text.
const JURISDICTION_ALIASES: Record<string, string> = {
  "Northern District of Mississippi": "Northern/Southern District of Mississippi",
  "Southern District of Mississippi": "Northern/Southern District of Mississippi",
};

/**
 * The supported district named EARLIEST in the text (i.e. the caption), not the
 * first one in list order — a protocol that cites another court's case law
 * must not be detected as that court.
 */
function detectJurisdictionInText(text: string): string {
  const lower = text.toLowerCase();
  const phrases: [string, string][] = [
    ...Object.keys(JURISDICTION_CODES).map((n): [string, string] => [n, n]),
    ...Object.entries(JURISDICTION_ALIASES),
  ];
  let best = "";
  let bestIdx = -1;
  let bestLen = 0;
  for (const [phrase, name] of phrases) {
    const i = lower.indexOf(phrase.toLowerCase());
    if (i === -1) continue;
    if (bestIdx === -1 || i < bestIdx || (i === bestIdx && phrase.length > bestLen)) {
      best = name;
      bestIdx = i;
      bestLen = phrase.length;
    }
  }
  return best;
}

const App = (): JSX.Element => {
  // State variables
  const [docStatus, setDocStatus] = useState<DocStatus>(null);
  const [docType, setDocType] = useState<DocType>(null);
  const selectedDocTypeRef = useRef<DocumentType | null>(null);
  const [flowStep, setFlowStep] = useState<FlowStep>("initial");
  const [jurisdiction, setJurisdiction] = useState<string>("");
  // Code of the district the current analysis is using (same precedence as the
  // Jurisdiction Check itself: the detected district, else the chosen one).
  const activeJurisdictionCode = (): string | undefined =>
    JURISDICTION_CODES[detectedJurisdiction?.trim() ? detectedJurisdiction : jurisdiction] || undefined;
  const [detectedJurisdiction, setDetectedJurisdiction] = useState<string>("");
  const [analysis, setAnalysis] = useState<string>("");
  // Structured issues from jurisdiction check (more stable than text parsing)
  const [jurisdictionIssues, setJurisdictionIssues] = useState<JurisdictionIssue[]>([]);

  // PERF: Track highlighted paragraphs to avoid expensive wildcard search when clearing
  // Store paragraph text keys (not proxy objects) to avoid cross-context issues
  const highlightedParagraphsRef = useRef<string[]>([]);
  // Identity (file URL) of the last document we analyzed, so we can detect when
  // the user has switched to a DIFFERENT document in the same task-pane session
  // and clear per-document analysis state that would otherwise carry over.
  // Pre-analysis prompt-injection check. The user's decision (exclude / analyze
  // anyway) is remembered for the exact document text it was made on.
  type InjectionChoice = "exclude" | "anyway" | "cancel";
  const [injectionReview, setInjectionReview] = useState<{
    findings: InjectionFinding[];
    partial: boolean;
    resolve: (choice: InjectionChoice) => void;
  } | null>(null);
  const [injectionScanning, setInjectionScanning] = useState(false);
  const [injectionNotice, setInjectionNotice] = useState("");
  const injectionDecisionRef = useRef<{ hash: string; quotes: string[]; decision: "clean" | "exclude" | "anyway" } | null>(null);
  const injectionInflightRef = useRef<{ hash: string; promise: Promise<string | null> } | null>(null);
  const [clauseRunStatus, setClauseRunStatus] = useState<"ok" | "failed" | "cancelled">("ok");
  const lastDocIdentityRef = useRef<string>(
    (() => {
      try {
        return sessionStorage.getItem("esi_doc_identity") || "";
      } catch {
        return "";
      }
    })()
  );

  // Word.run() queue: serializes all Word API calls to prevent overlapping contexts
  // that can deadlock the document and block editing.
  const wordRunQueueRef = useRef<Promise<unknown>>(Promise.resolve());
  const WORD_RUN_TIMEOUT = 30000; // 30s hard timeout per operation
  const queuedWordRun = async <T,>(fn: (context: Word.RequestContext) => Promise<T>): Promise<T> => {
    return new Promise<T>((resolve, reject) => {
      let settled = false;
      const timer = setTimeout(() => {
        if (!settled) {
          settled = true;
          console.error("[PERF] Word operation timed out after " + WORD_RUN_TIMEOUT + "ms");
          reject(new Error("Word operation timed out. The document may be too large or Office is unresponsive."));
        }
      }, WORD_RUN_TIMEOUT);

      wordRunQueueRef.current = wordRunQueueRef.current
        .catch((err) => { console.warn("[Queue] Previous operation failed:", err?.message || err); })
        .then(() => Word.run(fn))
        .then(
          (result) => { if (!settled) { settled = true; clearTimeout(timer); resolve(result); } },
          (err) => { if (!settled) { settled = true; clearTimeout(timer); reject(err); } }
        );
    });
  };

  const [selectedBulletIndex, setSelectedBulletIndex] = useState<number | null>(null);
  const [selectedBulletContent, setSelectedBulletContent] = useState<string>("");

  // Map bullet (using bullet text as key) to final corrected text
  // Initialize from sessionStorage to persist across navigation
  const [corrections, setCorrections] = useState<{ [key: string]: string }>(() => {
    try {
      const cached = sessionStorage.getItem('esi_corrections');
      if (cached) {
        const parsed = JSON.parse(cached);
        console.log('[Cache] Loaded corrections from sessionStorage:', Object.keys(parsed).length, 'items');
        return parsed;
      }
      return {};
    } catch {
      return {};
    }
  });
  // Track pre-fetching state for jurisdiction check
  const [prefetchingJurisdiction, setPrefetchingJurisdiction] = useState<boolean>(false);

  // Court links loaded from CSV in Azure Blob (mapping district => URL)
  const [courtLinks, setCourtLinks] = useState<{ [key: string]: string }>({});

  // Clause Check states
  const [clauseAnalysis, setClauseAnalysis] = useState<string>("");
  // Structured issues from clause check (more stable than text parsing)
  const [clauseIssues, setClauseIssues] = useState<ClauseIssue[]>([]);
  const [clauseCheckComplete, setClauseCheckComplete] = useState<boolean>(false);
  const [clauseSessionId, setClauseSessionId] = useState<string | null>(null);
  const [clauseSelectedBulletIndex, setClauseSelectedBulletIndex] = useState<number | null>(null);
  const [clauseSelectedBulletContent, setClauseSelectedBulletContent] = useState<string>("");
  // Initialize clauseCorrections from sessionStorage to persist across navigation
  const [clauseCorrections, setClauseCorrections] = useState<{ [key: string]: string }>(() => {
    try {
      const cached = sessionStorage.getItem('esi_clause_corrections');
      return cached ? JSON.parse(cached) : {};
    } catch {
      return {};
    }
  });
  // Track pre-fetching state for clause check
  const [prefetchingClause, setPrefetchingClause] = useState<boolean>(false);

  // Editor flaws - Initialize from sessionStorage to persist across navigation
  const [editorFlaws, setEditorFlaws] = useState<EditorFlaw[]>(() => {
    try {
      const cached = sessionStorage.getItem('esi_editor_flaws');
      return cached ? JSON.parse(cached) : [];
    } catch {
      return [];
    }
  });

  // Accepted corrections - Initialize from sessionStorage to persist
  const [acceptedCorrections, setAcceptedCorrections] = useState<AcceptedCorrection[]>(() => {
    try {
      const cached = sessionStorage.getItem('esi_accepted_corrections');
      return cached ? JSON.parse(cached) : [];
    } catch {
      return [];
    }
  });

  // AI-generated talking points summary
  const [talkingPointsSummary, setTalkingPointsSummary] = useState<string>("");
  const [talkingPointsLoading, setTalkingPointsLoading] = useState<boolean>(false);
  // Snapshot of document text when the add-in first inspects the document (plugin start)
  const [snapshotAtPluginStart, setSnapshotAtPluginStart] = useState<string>("");
  // Snapshot of document text when Jurisdiction Check (analysisComplete) first appears
  const [snapshotAtFlowStart, setSnapshotAtFlowStart] = useState<string>('');
  // Snapshot of document text before ESI Flow Editor stage
  const [snapshotBeforeEditor, setSnapshotBeforeEditor] = useState<string>('');
  // Track if Jurisdiction analysis has run
  const [jurisdictionRun, setJurisdictionRun] = useState<boolean>(false);
  // Track if ESI Flow Editor stage has started
  const [editorRun, setEditorRun] = useState<boolean>(false);
  // Track expansion of Jurisdiction summary card
  const [jurisdictionSummaryExpanded, setJurisdictionSummaryExpanded] = useState<boolean>(true);

  // Track which ESI Flow Editor suggestion is expanded
  const [expandedFlawIndex, setExpandedFlawIndex] = useState<number | null>(null);

  // Track the currently highlighted section
  const [highlightedSection, setHighlightedSection] = useState<number | null>(null);
  const [showTrackChangesWarning, setShowTrackChangesWarning] = useState<boolean>(false);

  // Uploaded templates state
  const [clientName, setClientName] = useState<string>("");
  const [projectNumber, setProjectNumber] = useState<string>("");
  const [uploadedTemplates, setUploadedTemplates] = useState<{
    protocol?: Array<{ documentType: string; fileName: string; url: string }>;
    protective?: Array<{ documentType: string; fileName: string; url: string }>;
    clause?: Array<{ documentType: string; fileName: string; url: string }>;
  }>({});

  // Analyzing (Scorecard) state
  const [analyzeLoading, setAnalyzeLoading] = useState(false);
  const [analyzeError, setAnalyzeError] = useState("");
  const [scorecard, setScorecard] = useState<Scorecard | null>(null);
  // Baseline scorecard captured the first time analysis runs
  const [baselineScorecard, setBaselineScorecard] = useState<Scorecard | null>(null);

  // Client Checklist state
  const [checklistQuestions, setChecklistQuestions] = useState<{question:string, sectionId:string}[]>([]);
  const [checklistGroups, setChecklistGroups] = useState<Record<string, {label:string, items:{q:string, checked:boolean}[] }>>({});
  const [boostSections, setBoostSections] = useState<Record<string, boolean>>({});

  

  const manualJurisdictions = Object.keys(JURISDICTION_CODES);

  // Load clientName from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('clientName');
    if (saved) setClientName(saved);
  }, []);

  // Load projectNumber from localStorage on mount
  useEffect(() => {
    const savedNumber = localStorage.getItem('projectNumber');
    if (savedNumber) setProjectNumber(savedNumber);
  }, []);

  // PERF: Debounce refs for sessionStorage writes to reduce JSON serialization overhead
  const correctionsDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clauseCorrectionsDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Persist corrections to sessionStorage whenever they change (DEBOUNCED)
  useEffect(() => {
    if (Object.keys(corrections).length > 0) {
      // Clear any pending save
      if (correctionsDebounceRef.current) {
        clearTimeout(correctionsDebounceRef.current);
      }
      // Debounce: wait 500ms before saving
      correctionsDebounceRef.current = setTimeout(() => {
        try {
          sessionStorage.setItem('esi_corrections', JSON.stringify(corrections));
          console.log('[Cache] Saved corrections to sessionStorage:', Object.keys(corrections).length, 'items');
        } catch (e) {
          console.warn('[Cache] Failed to save corrections to sessionStorage:', e);
        }
      }, 500);
    }
    return () => {
      if (correctionsDebounceRef.current) {
        clearTimeout(correctionsDebounceRef.current);
      }
    };
  }, [corrections]);

  // Persist clauseCorrections to sessionStorage whenever they change (DEBOUNCED)
  useEffect(() => {
    if (Object.keys(clauseCorrections).length > 0) {
      // Clear any pending save
      if (clauseCorrectionsDebounceRef.current) {
        clearTimeout(clauseCorrectionsDebounceRef.current);
      }
      // Debounce: wait 500ms before saving
      clauseCorrectionsDebounceRef.current = setTimeout(() => {
        try {
          sessionStorage.setItem('esi_clause_corrections', JSON.stringify(clauseCorrections));
          console.log('[Cache] Saved clauseCorrections to sessionStorage:', Object.keys(clauseCorrections).length, 'items');
        } catch (e) {
          console.warn('[Cache] Failed to save clauseCorrections to sessionStorage:', e);
        }
      }, 500);
    }
    return () => {
      if (clauseCorrectionsDebounceRef.current) {
        clearTimeout(clauseCorrectionsDebounceRef.current);
      }
    };
  }, [clauseCorrections]);

  // Persist editorFlaws to sessionStorage whenever they change
  useEffect(() => {
    try {
      sessionStorage.setItem('esi_editor_flaws', JSON.stringify(editorFlaws));
      console.log('[Cache] Saved editorFlaws to sessionStorage:', editorFlaws.length, 'items');
    } catch (e) {
      console.warn('[Cache] Failed to save editorFlaws to sessionStorage:', e);
    }
  }, [editorFlaws]);

  // Persist acceptedCorrections to sessionStorage whenever they change
  useEffect(() => {
    try {
      sessionStorage.setItem('esi_accepted_corrections', JSON.stringify(acceptedCorrections));
      console.log('[Cache] Saved acceptedCorrections to sessionStorage:', acceptedCorrections.length, 'items');
    } catch (e) {
      console.warn('[Cache] Failed to save acceptedCorrections to sessionStorage:', e);
    }
  }, [acceptedCorrections]);

  // Fetch uploaded templates when clientName changes
  useEffect(() => {
    if (clientName && projectNumber) {
      apiFetch(`${API_BASE}/api/clientTemplates?clientName=${encodeURIComponent(clientName)}&projectNumber=${encodeURIComponent(projectNumber)}`)
        .then(res => res.json())
        .then((data: unknown) => {
          // F9: Runtime validation of API response structure
          if (!Array.isArray(data)) {
            console.error('Invalid response format: expected array');
            return;
          }
          const validatedData = data.filter((item): item is { documentType: string; fileName: string; url: string } =>
            item && typeof item === 'object' &&
            typeof item.documentType === 'string' &&
            typeof item.fileName === 'string' &&
            typeof item.url === 'string'
          );
          const templates = {
            protocol: validatedData.filter(t => t.documentType === 'protocol'),
            protective: validatedData.filter(t => t.documentType === 'protective'),
            clause: validatedData.filter(t => t.documentType === 'clause')
          };
          setUploadedTemplates(templates);
        })
        .catch(err => console.error('Error fetching uploaded templates:', err));
    }
  }, [clientName, projectNumber]);

  // Optimized batch highlighting function - performs all searches in a single Word.run() to prevent flickering
  // KEY OPTIMIZATION: Minimizes context.sync() calls by batching all operations
  // PERF: Tracks highlighted paragraphs for fast clearing
  const highlightMultipleTextsBatch = async (_sectionNumber: number, textsToHighlight: string[]): Promise<boolean> => {
    if (!textsToHighlight || textsToHighlight.length === 0) {
      return false;
    }

    // PERF: Clear previous tracking (new highlights will replace old ones)
    highlightedParagraphsRef.current = [];

    let textFound = false;

    await queuedWordRun(async (context) => {
      const body = context.document.body;
      const allSearchResults: Word.RangeCollection[] = [];
      const allParagraphsToHighlight: Word.Paragraph[] = [];

      // PHASE 1: Collect all search operations (no sync yet)
      for (const textToHighlight of textsToHighlight) {
        if (!textToHighlight || textToHighlight.trim().length < 4) continue;

        // Build search candidates (prioritize most likely matches)
        const candidateStrings: string[] = [
          textToHighlight.trim(),
          textToHighlight.replace(/[""]/g, '"').trim(),
          textToHighlight.replace(/"/g, "'").trim(),
          textToHighlight.replace(/'/g, '"').trim(),
        ].filter(c => c.length >= 4);

        // Queue all searches (don't sync yet)
        for (const cand of candidateStrings) {
          try {
            const results = body.search(cand, { matchCase: false });
            results.load('items');
            allSearchResults.push(results);
          } catch (e) {
            // Skip if search setup fails
          }
        }

        // Wildcard fallback
        const buildWildcard = (input: string) => {
          let s = input.replace(/^"|"$/g, '').replace(/^'|'$/g, '');
          s = s.replace(/\s+/g, '*').replace(/•/g, '*').replace(/\*+/g, '*');
          return s.trim();
        };

        const wild1 = buildWildcard(textToHighlight);
        if (wild1.length > 3) {
          try {
            const wildResults = body.search(wild1, { matchCase: false, matchWildcards: true });
            wildResults.load('items');
            allSearchResults.push(wildResults);
          } catch (e) {
            // Skip if wildcard search setup fails
          }
        }
      }

      // PHASE 2: Single sync to get all search results at once
      await context.sync();

      // PHASE 3: Collect all matching ranges and their paragraphs (deduplicate using array)
      for (const results of allSearchResults) {
        if (results.items.length > 0) {
          textFound = true;
          for (const range of results.items) {
            try {
              const para = range.paragraphs.getFirst();
              para.load('text');
              // Check if paragraph already in array to avoid duplicates
              let alreadyAdded = false;
              for (let i = 0; i < allParagraphsToHighlight.length; i++) {
                if (allParagraphsToHighlight[i] === para) {
                  alreadyAdded = true;
                  break;
                }
              }
              if (!alreadyAdded) {
                allParagraphsToHighlight.push(para);
              }
            } catch {
              // Fallback: highlight range directly
              range.font.highlightColor = '#FFFF00';
            }
          }
        }
      }

      // PHASE 4: Load all paragraph texts in one sync
      if (allParagraphsToHighlight.length > 0) {
        await context.sync();

        // PHASE 5: Highlight all paragraphs at once (deduplicated by text content)
        // Store text keys (not proxy objects) to avoid cross-context issues in clearAllHighlights
        const seenTexts = new Set<string>();
        let firstPara: Word.Paragraph | null = null;
        const newHighlightedTexts: string[] = [];

        for (let i = 0; i < allParagraphsToHighlight.length; i++) {
          const para = allParagraphsToHighlight[i];
          const paraText = para.text.substring(0, 100);
          if (!seenTexts.has(paraText)) {
            para.font.highlightColor = '#FFFF00';
            seenTexts.add(paraText);
            newHighlightedTexts.push(paraText);
            if (!firstPara) firstPara = para;
          }
        }

        // Store text keys for clearAllHighlights()
        highlightedParagraphsRef.current = newHighlightedTexts;

        // Select first paragraph for focus
        if (firstPara) {
          firstPara.select();
        }
      }

      // PHASE 6: Final sync to apply all highlights at once
      await context.sync();
    });

    setHighlightedSection(-1);
    return textFound;
  };

  // Modified highlightSpecificText function to handle multi-line and bullet/point formatting
  // OPTIMIZED: Now uses batch highlighting when called multiple times
  const highlightSpecificText = async (sectionNumber: number, textToHighlight: string | null = null): Promise<boolean> => {
    if (!textToHighlight) {
      return highlightSection(sectionNumber);
    }
    // Use batch function for single text (more efficient)
    return highlightMultipleTextsBatch(sectionNumber, [textToHighlight]);
  };

  // Fallback: highlight a section by number when text search fails.
  // PERF: Previous-highlight clearing is removed since clearAllHighlights() is always
  // called before this function. Highlighted paragraphs are now tracked in
  // highlightedParagraphsRef for efficient clearing later.
  const highlightSection = async (sectionNumber: number): Promise<boolean> => {
    await queuedWordRun(async (context) => {
      const body = context.document.body;
      const paragraphs = body.paragraphs;
      paragraphs.load("items/text");
      await context.sync();

      const targetNumStr = sectionNumber.toString();
      let startIdx = -1;
      let endIdx = -1;
      // Find the start of the section
      for (let i = 0; i < paragraphs.items.length; i++) {
        const paraText = paragraphs.items[i].text.trim();
        if (
          paraText.startsWith(targetNumStr + " ") ||
          paraText.startsWith(targetNumStr + ".") ||
          paraText.startsWith(targetNumStr + ":")
        ) {
          startIdx = i;
          break;
        }
      }
      if (startIdx === -1) return; // Section not found
      // Find the end of the section: next section heading or blank line
      endIdx = startIdx;
      for (let i = startIdx + 1; i < paragraphs.items.length; i++) {
        const paraText = paragraphs.items[i].text.trim();
        if (/^\d+(\.|:| )/.test(paraText) || paraText === "") {
          break;
        }
        endIdx = i;
      }
      // Highlight all paragraphs in the section and track them (text keys, not proxies)
      for (let i = startIdx; i <= endIdx; i++) {
        paragraphs.items[i].font.highlightColor = "#FFFF00";
        highlightedParagraphsRef.current.push(paragraphs.items[i].text.substring(0, 100));
      }
      // Select the first paragraph for focus
      paragraphs.items[startIdx].select(Word.SelectionMode.start);
      await context.sync();
    });
    setHighlightedSection(sectionNumber);
    return true;
  };

  // New helper to find heading text based on an anchor snippet
  // PERF: Uses split-loop pattern - batch load all paragraph styles in one sync
  // instead of calling context.sync() per search result (N syncs → 2 syncs).
  async function findHeadingText(anchor: string): Promise<string | null> {
    return queuedWordRun(async (ctx) => {
      const body = ctx.document.body;
      const headingSearch = body.search(anchor, { matchCase: false });
      headingSearch.load("items");
      await ctx.sync();

      // Batch-load all paragraph styles + text in one go (split-loop pattern)
      const paragraphs: Word.Paragraph[] = [];
      for (const item of headingSearch.items) {
        const para = item.paragraphs.getFirst();
        para.load("style,text");
        paragraphs.push(para);
      }

      if (paragraphs.length > 0) {
        await ctx.sync(); // single sync for all paragraph properties
      }

      // Now iterate without syncing - data is already loaded
      for (const para of paragraphs) {
        if (para.style && para.style.startsWith("Heading")) {
          return para.text.trim();
        }
      }

      if (paragraphs.length > 0) {
        return paragraphs[0].text.trim();
      }
      return null;
    });
  }

  // Helper to extract anchor from a bullet text; looks for text inside "Anchor: ..."
  function extractAnchorFromBullet(bullet: string): string | null {
    const match = bullet.match(/Anchor:\s*([^\n]*)/);
    return match ? match[1].trim() : null;
  }

  // Full scorecard used on the main Analyzing page
  function renderLine(status: AnchorStatus, passTxt: string, warnTxt: string, failTxt: string) {
    // Phrases already include their own status emoji. Avoid appending extra icons.
    if (status === "pass") return `• ${passTxt}`;
    if (status === "fail") return `• ${failTxt}`;
    return `• ${warnTxt}`;
  }

  function ScorecardBlock({ c }: { c: Scorecard }) {
    const [summaryExpanded, setSummaryExpanded] = useState<boolean>(true);
    const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
      prop: false, cost: false, priv: false, gov: false, disp: false, conf: false, use: false,
    });

    const toggleSection = (id: string) => {
      setExpandedSections((prev) => ({ ...prev, [id]: !prev[id] }));
    };

    const sectionsById: Record<string, Section> = Object.fromEntries(c.sections.map(s => [s.id, s]));
    const ids = c.sections.map(s => s.id);
    const isProtective = ids.includes("conf") || ids.includes("use");
    const title = isProtective ? "Protective Order Risk Scorecard" : "ESI Protocol Risk Scorecard";
    const overallHeader = isProtective ? "Overall Protective Order Risk Profile" : "Overall Protocol Risk Profile";
    const S = sectionsById as any;
    const scoreColor = (score: number) => score >= 80 ? "text-brand-500" : score >= 60 ? "text-gold" : "text-coral";

    // Reusable scorecard section renderer
    const renderScorecardSection = (num: number, sectionData: Section | undefined, bullets: React.ReactNode) => {
      if (!sectionData) return null;
      const id = sectionData.id;
      return (
        <div className="mt-2 rounded-lg border border-gray-200 bg-white overflow-hidden">
          <div
            className="flex items-center gap-2 px-3 py-2.5 cursor-pointer hover:bg-gray-50 transition-colors"
            onClick={() => toggleSection(id)}
          >
            <span className="text-xs text-gray-400">{expandedSections[id] ? "\u25BC" : "\u25B6"}</span>
            <span className="font-semibold text-sm text-gray-900">{`${num}. ${sectionData.title}`}</span>
            <span className={`ml-auto text-sm font-bold ${scoreColor(sectionData.score)}`}>{sectionData.score}/100</span>
          </div>
          {expandedSections[id] && (
            <div className="px-4 py-2.5 border-t border-gray-100 text-sm text-gray-700 space-y-1">
              {bullets}
              <div className="mt-2 pt-2 border-t border-gray-100 font-medium">
                <span className="text-brand-600">Recommendation:</span> {sectionData.recommendation}
              </div>
            </div>
          )}
        </div>
      );
    };

    return (
      <div className="space-y-3">
        <div className="text-base font-bold text-gray-900">{title}</div>
        <div className="text-xs text-gray-500 italic">Analysis Mode: Defense Counsel Viewpoint</div>

        <div className="font-semibold text-sm text-gray-800">{overallHeader}</div>
        <div className="flex items-center gap-3">
          <div className={`text-2xl font-bold ${scoreColor(c.composite)}`}>
            {c.composite}<span className="text-sm text-gray-500">/100</span>
          </div>
          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold text-white ${
            c.compositeLabel === "Low" ? "bg-brand-500" : c.compositeLabel === "Moderate" ? "bg-gold" : "bg-coral"
          }`}>
            {c.compositeLabel} Risk
          </span>
        </div>

        {/* Collapsible Summary */}
        <div
          className="rounded-lg bg-gray-50 border border-gray-200 cursor-pointer"
          onClick={() => setSummaryExpanded((prev) => !prev)}
        >
          <div className="flex items-center gap-2 px-3 py-2 font-semibold text-sm text-gray-800">
            <span className="text-xs text-gray-400">{summaryExpanded ? "\u25BC" : "\u25B6"}</span>
            <span>Summary</span>
          </div>
          {summaryExpanded && (
            <div className="px-4 pb-3 text-sm text-gray-700 leading-relaxed">{c.summary}</div>
          )}
        </div>

        {isProtective ? (
          <>
            {renderScorecardSection(1, S.conf, <>
              <div>{renderLine((S.conf?.bullets as any)?.multiTier, "More than one confidentiality tier (\u2705).", "Confidentiality tiers unclear (\u26A0\uFE0F).", "Only one confidentiality tier (\u26A0\uFE0F).")}</div>
              <div>{renderLine((S.conf?.bullets as any)?.inHouseScope, "In-house access rules defined (\u2705).", "In-house access unclear (\u26A0\uFE0F).", "No restrictions for in-house personnel access (\u26A0\uFE0F).")}</div>
              <div>{renderLine((S.conf?.bullets as any)?.depoFiling, "Depositions/filings treatment addressed (\u2705).", "Depositions/filings treatment unclear (\u26A0\uFE0F).", "Silent on deposition/filing protections (\u26A0\uFE0F).")}</div>
              <div>{renderLine((S.conf?.bullets as any)?.litigationOnly, "Use limited to this litigation (\u2705).", "Scope of use unclear (\u26A0\uFE0F).", "No 'this litigation only' limitation (\u26A0\uFE0F).")}</div>
            </>)}
            {renderScorecardSection(2, S.priv, <>
              <div>{renderLine((S.priv?.bullets as any)?.rule502d, "Rule 502(d) clause present (\u2705).", "502(d) reference unclear (\u26A0\uFE0F).", "Rule 502(d) clause missing (\u274C).")}</div>
              <div>{renderLine((S.priv?.bullets as any)?.clawbackMechanics, "Clawback mechanics clear (\u2705).", "Clawback mechanics vague (\u26A0\uFE0F).", "No clear clawback mechanics (\u274C).")}</div>
              <div>{renderLine((S.priv?.bullets as any)?.inadvertent, "Inadvertent disclosure addressed (\u2705).", "Inadvertent disclosure unclear (\u26A0\uFE0F).", "Inadvertent disclosure not addressed (\u274C).")}</div>
              <div>{renderLine((S.priv?.bullets as any)?.noWaiver, "No-waiver by disclosure safeguard present (\u2705).", "No-waiver safeguard unclear (\u26A0\uFE0F).", "No 'no-waiver by disclosure' safeguard (\u274C).")}</div>
            </>)}
            {renderScorecardSection(3, S.use, <>
              <div>{renderLine((S.use?.bullets as any)?.litOnly, "Use limited to this litigation (\u2705).", "Scope of use unclear (\u26A0\uFE0F).", "No 'this litigation only' limitation (\u274C).")}</div>
              <div>{renderLine((S.use?.bullets as any)?.expertVetting, "Expert/vendor vetting rights included (\u2705).", "Expert/vendor vetting unclear (\u26A0\uFE0F).", "Expert/vendor vetting absent (\u26A0\uFE0F).")}</div>
              <div>{renderLine((S.use?.bullets as any)?.security, "Security measures specified (\u2705).", "Security measures unclear (\u26A0\uFE0F).", "No encryption/security measures specified (\u274C).")}</div>
              <div>{renderLine((S.use?.bullets as any)?.byod, "BYOD/cloud storage controls present (\u2705).", "BYOD/cloud controls unclear (\u26A0\uFE0F).", "Silent on BYOD/cloud storage (\u274C).")}</div>
            </>)}
            {renderScorecardSection(4, S.gov, <>
              <div>{renderLine((S.gov?.bullets as any)?.crossBorder, "Cross-border transfer restrictions addressed (\u2705).", "Cross-border obligations unclear (\u26A0\uFE0F).", "Cross-border transfers not addressed (\u26A0\uFE0F).")}</div>
              <div>{renderLine((S.gov?.bullets as any)?.sensitive, "Sensitive data categories defined (\u2705).", "Sensitive data handling unclear (\u26A0\uFE0F).", "No extra protections for PII/HR data (\u274C).")}</div>
              <div>{renderLine((S.gov?.bullets as any)?.returnDestroy, "Return/destruction protocol included (\u2705).", "Return/destruction unclear (\u26A0\uFE0F).", "No return/destruction protocol (\u274C).")}</div>
              <div>{renderLine((S.gov?.bullets as any)?.complianceAlign, "Compliance alignment (e.g., GDPR) addressed (\u2705).", "Compliance alignment unclear (\u26A0\uFE0F).", "No reference to compliance alignment (\u274C).")}</div>
            </>)}
            {renderScorecardSection(5, S.disp, <>
              <div>{renderLine((S.disp?.bullets as any)?.challengeProcess, "Challenge process for designations included (\u2705).", "Challenge process unclear (\u26A0\uFE0F).", "No process for challenging designations (\u26A0\uFE0F).")}</div>
              <div>{renderLine((S.disp?.bullets as any)?.objectionDeadlines, "Deadlines for objections defined (\u2705).", "Objection deadlines unclear (\u26A0\uFE0F).", "Deadlines for objections missing (\u26A0\uFE0F).")}</div>
              <div>{renderLine((S.disp?.bullets as any)?.burdenOnDesignator, "Burden on designating party (\u2705).", "Burden allocation unclear (\u26A0\uFE0F).", "Burden not on designating party (\u26A0\uFE0F).")}</div>
              <div>{renderLine((S.disp?.bullets as any)?.escalation, "Escalation ladder before motion practice (\u2705).", "Escalation framework unclear (\u26A0\uFE0F).", "No escalation ladder before motion practice (\u26A0\uFE0F).")}</div>
            </>)}
          </>
        ) : (
          <>
            {(() => {
              const lookup: Record<string, Section> = Object.fromEntries(c.sections.map(s => [s.id, s]));
              const sProp = lookup["prop"], sCost = lookup["cost"], sPriv = lookup["priv"], sGov = lookup["gov"], sDisp = lookup["disp"];
              const bProp = sProp.bullets as SectionBulletsProp;
              const bCost = sCost.bullets as SectionBulletsCost;
              const bPriv = sPriv.bullets as SectionBulletsPriv;
              const bGov = sGov.bullets as SectionBulletsGov;
              const bDisp = sDisp.bullets as SectionBulletsDisp;
              return (
                <>
                  {renderScorecardSection(1, sProp, <>
                    <div>{renderLine(bProp.custodianCap, "Custodian limits present (\u2705).", "Custodian limits unclear (\u26A0\uFE0F).", "Custodian limits missing (\u26A0\uFE0F).")}</div>
                    <div>{renderLine(bProp.temporalCutoff, "Temporal cut-off specified (\u2705).", "Temporal scope unclear (\u26A0\uFE0F).", "No temporal cut-off specified (\u26A0\uFE0F).")}</div>
                    <div>{renderLine(bProp.excludesNAS, "Excludes non-accessible sources (\u2705).", "Non-accessible exclusion unclear (\u26A0\uFE0F).", "Non-accessible sources not excluded (\u26A0\uFE0F).")}</div>
                    <div>{renderLine(bProp.searchMethod, "Search methodology defined (\u2705).", "Search approach unclear (\u26A0\uFE0F).", "Search methodology not defined (\u26A0\uFE0F).")}</div>
                  </>)}
                  {renderScorecardSection(2, sCost, <>
                    <div>{renderLine(bCost.globalDedupe, "Global deduplication specified (\u2705).", "Deduplication unclear (\u26A0\uFE0F).", "No global deduplication (\u26A0\uFE0F).")}</div>
                    <div>{renderLine(bCost.TAR, "TAR/predictive coding addressed (\u2705).", "TAR unclear (\u26A0\uFE0F).", "No TAR provision (\u26A0\uFE0F).")}</div>
                    <div>{renderLine(bCost.metadataOverbroad, "Metadata scope appropriate (\u2705).", "Metadata scope unclear (\u26A0\uFE0F).", "Metadata scope overbroad (\u26A0\uFE0F).")}</div>
                    <div>{renderLine(bCost.costShift, "Cost-shifting addressed (\u2705).", "Cost allocation unclear (\u26A0\uFE0F).", "No cost-shifting provisions (\u26A0\uFE0F).")}</div>
                  </>)}
                  {renderScorecardSection(3, sPriv, <>
                    <div>{renderLine(bPriv.rule502d, "Rule 502(d) clause present (\u2705).", "502(d) reference unclear (\u26A0\uFE0F).", "Rule 502(d) clause missing (\u274C).")}</div>
                    <div>{(bPriv as SectionBulletsPriv).clawbackVague === "fail" ? "\u2022 Clawback vague (\u26A0\uFE0F)." : "\u2022 Clawback clear (\u2705)."} </div>
                    <div>{renderLine(bPriv.confTiers, "Confidentiality tiers present (\u2705).", "Confidentiality framework unclear (\u26A0\uFE0F).", "Confidentiality tiers missing (\u26A0\uFE0F).")}</div>
                    <div>{renderLine(bPriv.inadvertent, "Inadvertent disclosure addressed (\u2705).", "Inadvertent disclosure unclear (\u26A0\uFE0F).", "Inadvertent disclosure not addressed (\u274C).")}</div>
                  </>)}
                  {renderScorecardSection(4, sGov, <>
                    <div>{renderLine(bGov.crossBorder, "Cross-border transfers addressed (\u2705).", "Cross-border obligations unclear (\u26A0\uFE0F).", "Cross-border transfers not addressed (\u26A0\uFE0F).")}</div>
                    <div>{renderLine((bGov as SectionBulletsGov).byod, "BYOD/cloud storage addressed (\u2705).", "BYOD/cloud storage unclear (\u26A0\uFE0F).", "Silent on BYOD/cloud storage (\u26A0\uFE0F).")}</div>
                    <div>{renderLine(bGov.sensitive, "Sensitive data categories defined (\u2705).", "Sensitive data handling unclear (\u26A0\uFE0F).", "No extra protections for PII/HR data (\u26A0\uFE0F).")}</div>
                    <div>{renderLine(bGov.retention, "Retention/closure addressed (\u2705).", "Retention unclear (\u26A0\uFE0F).", "No retention/closure plan (\u26A0\uFE0F).")}</div>
                  </>)}
                  {renderScorecardSection(5, sDisp, <>
                    <div>{renderLine(bDisp.meetConfer, "Meet-and-confer before motion practice (\u2705).", "Meet-and-confer unclear (\u26A0\uFE0F).", "No meet-and-confer step (\u26A0\uFE0F).")}</div>
                    <div>{renderLine(bDisp.escalation, "Escalation ladder present (\u2705).", "Escalation unclear (\u26A0\uFE0F).", "No escalation ladder (\u26A0\uFE0F).")}</div>
                    <div>{renderLine(bDisp.disputeTiming, "Dispute timing/deadlines defined (\u2705).", "Dispute timing unclear (\u26A0\uFE0F).", "No dispute timing/deadlines (\u26A0\uFE0F).")}</div>
                    <div>{renderLine(bDisp.specialMaster, "Special master/neutral option present (\u2705).", "Neutral appointment unclear (\u26A0\uFE0F).", "No neutral appointment option (\u26A0\uFE0F).")}</div>
                  </>)}
                </>
              );
            })()}
          </>
        )}

        {/* Key Risks */}
        <div className="mt-4 pt-3 border-t border-gray-200">
          <div className="font-bold text-sm text-gray-900 mb-2">Key Risks for Defense Counsel</div>
          {c.keyRisks && c.keyRisks.length > 0 ? (
            <ol className="list-decimal list-inside space-y-1 text-sm text-gray-700">
              {c.keyRisks.map((r, i) => <li key={i}>{r}</li>)}
            </ol>
          ) : (
            <p className="text-sm text-gray-500">No specific high-risk items identified.</p>
          )}
        </div>
      </div>
    );
  }

  // Compact scorecard used only at the bottom of the ESI Flow Editor
  function CompactScorecardBlock({ c }: { c: Scorecard }) {
    const sectionsById: Record<string, Section> = Object.fromEntries(c.sections.map(s => [s.id, s]));
    const ids = c.sections.map(s => s.id);
    const isProtective = ids.includes("conf") || ids.includes("use");
    const title = isProtective ? "Protective Order Risk Scorecard" : "ESI Protocol Risk Scorecard";
    const overallHeader = isProtective ? "Overall Protective Order Risk Profile" : "Overall Protocol Risk Profile";

    const mainSectionIds = isProtective
      ? ["conf", "priv", "use", "gov", "disp"]
      : ["prop", "cost", "priv", "gov", "disp"];

    const mainSections: Section[] = mainSectionIds
      .map((id) => sectionsById[id])
      .filter((s): s is Section => !!s);

    let summaryText = "";
    if (!mainSections.length) {
      summaryText = "No section scores are available yet.";
    } else {
      const sortedByScore = [...mainSections].sort((a, b) => a.score - b.score);
      const worstSections = sortedByScore.slice(0, 2);
      const worstTitles = worstSections.map((s) => s.title).join(", ");

      if (c.composite >= 80) {
        summaryText = "Draft looks strong overall with only minor gaps.";
      } else if (c.composite >= 60) {
        summaryText = `Moderate risk: important gaps in ${worstTitles}.`;
      } else {
        summaryText = `Score is low due to significant gaps in ${worstTitles}.`;
      }
    }

    const scoreColor = (score: number) => score >= 80 ? "text-brand-500" : score >= 60 ? "text-gold" : "text-coral";

    return (
      <div className="space-y-2 rounded-lg border border-gray-200 bg-gray-50 p-3">
        <div className="text-sm font-bold text-gray-900">{title}</div>
        <div className="text-xs text-gray-800 font-semibold">{overallHeader}</div>
        <div className="flex items-center gap-2">
          <span className={`text-lg font-bold ${scoreColor(c.composite)}`}>{c.composite}/100</span>
          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold text-white ${
            c.compositeLabel === "Low" ? "bg-brand-500" : c.compositeLabel === "Moderate" ? "bg-gold" : "bg-coral"
          }`}>{c.compositeLabel} Risk</span>
        </div>
        <p className="text-xs text-gray-600">{summaryText}</p>
        <div className="space-y-1">
          {mainSections.map((s, idx) => (
            <div key={s.id} className="flex justify-between text-xs text-gray-700">
              <span>{idx + 1}. {s.title}</span>
              <span className={`font-semibold ${scoreColor(s.score)}`}>{s.score}/100</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Helper to extract the title from a bullet by removing bullet symbol and any anchor info
  function extractBulletTitle(bullet: string): string {
    let title = bullet.replace(/^•\s*/, '');
    title = title.replace(/\(Anchor:[^)]+\)/, '').trim();
    return title;
  }

  // Helper to format AI correction response (used in both click handlers and pre-fetch)
  function formatJurisdictionCorrection(analysisText: string): string {
    let formatted = analysisText
      .trim()
      .replace(/^•\s*Anchor:[^\n]*\n?/i, "")
      .replace(/(^|\n)\s*•?\s*Paragraph:/gi, "$1<strong>Paragraph:</strong>")
      .replace(/(^|\n)\s*•?\s*Analysis:/gi, "$1<strong>Analysis:</strong>")
      .replace(/(^|\n)\s*•?\s*Current text:/gi, "$1<strong>Current text:</strong>")
      .replace(/(^|\n)\s*•?\s*(Correction|Recommended Edits):/gi, "$1<strong>Recommended Edits:</strong>");

    let firstAnalysis = true;
    formatted = formatted.replace(/<strong>Analysis:<\/strong>/gi, (match) => {
      if (firstAnalysis) { firstAnalysis = false; return match; }
      return `<hr style="margin:8px 0;border-top:1px solid #e5e7eb;" />\n${match}`;
    });

    const corrMatch = formatted.match(/<strong>(?:Recommended Edits|Correction):<\/strong>\s*"([^"]*)"/i);
    if (corrMatch) {
      formatted = formatted.replace(
        /(<strong>(?:Recommended Edits|Correction):<\/strong>\s*")([^"]*)(")/i,
        `$1${corrMatch[1]}$3`
      );
    }
    return stripRecommendedEditsFormatting(formatted);
  }

  function formatClauseCorrection(analysisText: string): string {
    let formatted = analysisText.trim()
      .replace(/\s*\(Source:[^)]*\)/gi, "")
      .replace(/\s*\[Source:[^\]]*\]/gi, "")
      .replace(/(^|\n)\s*•?\s*Anchor:[^\n]*/gi, '')
      .trim()
      .replace(/(^|\n)\s*•?\s*Paragraph:/gi, "$1<strong>Paragraph:</strong>")
      .replace(/(^|\n)\s*•?\s*Analysis:/gi, "$1<strong>Analysis:</strong>")
      .replace(/(^|\n)\s*•?\s*Current text:/gi, "$1<strong>Current text:</strong>")
      .replace(/(^|\n)\s*•?\s*(Correction|Recommended Edits):/gi, "$1<strong>Recommended Edits:</strong>");

    let firstAnalysis = true;
    formatted = formatted.replace(/<strong>Analysis:<\/strong>/gi, (match) => {
      if (firstAnalysis) { firstAnalysis = false; return match; }
      return `<hr style="margin:8px 0;border-top:1px solid #e5e7eb;" />\n${match}`;
    });

    const corrMatch = formatted.match(/<strong>(?:Recommended Edits|Correction):<\/strong>\s*"([^"]*)"/i);
    if (corrMatch) {
      formatted = formatted.replace(
        /(<strong>(?:Recommended Edits|Correction):<\/strong>\s*")([^"]*)(")/i,
        `$1${corrMatch[1]}$3`
      );
    }
    formatted = formatted.replace(/(^|\n)\s*Document content:/gi, '$1Current text:');
    return stripRecommendedEditsFormatting(formatted);
  }

  // Pre-fetch all jurisdiction check bullet details in parallel
  const prefetchJurisdictionDetails = async (analysisText: string, fullDoc: string, currentDocType: DocType) => {
    // IMPORTANT: Use same transformation as render (trim) to ensure cache keys match
    const bullets = analysisText.split("\n").map((line) => line.trim()).filter((line) => line.startsWith("•"));
    if (bullets.length === 0) return;

    setPrefetchingJurisdiction(true);
    const startTime = Date.now();
    console.log(`[Prefetch] Starting parallel fetch for ${bullets.length} jurisdiction bullets at ${new Date().toISOString()}`);

    const fetchPromises = bullets.map(async (bullet, idx): Promise<{ bullet: string; correction: string } | null> => {
      const anchor = extractAnchorFromBullet(bullet) || extractBulletTitle(bullet);
      const fetchStart = Date.now();
      try {
        const data = await postFlawAnalysis({
          docContent: fullDoc,
          requirement: anchor,
          jurisdiction: activeJurisdictionCode(),
          clientName,
          documentType: currentDocType,
        });
        console.log(`[Prefetch] Bullet ${idx + 1} completed in ${Date.now() - fetchStart}ms: ${anchor}`);
        return { bullet, correction: formatJurisdictionCorrection(data.analysis) };
      } catch (err) {
        console.warn(`[Prefetch] Bullet ${idx + 1} failed after ${Date.now() - fetchStart}ms for anchor "${anchor}":`, err);
        return null;
      }
    });

    // Use Promise.all - errors already caught inside each promise
    const results = await Promise.all(fetchPromises);
    const newCorrections: { [key: string]: string } = {};

    for (const result of results) {
      if (result) {
        newCorrections[result.bullet] = result.correction;
      }
    }

    setCorrections((prev: { [key: string]: string }) => ({ ...prev, ...newCorrections }));
    setPrefetchingJurisdiction(false);
    const totalTime = Date.now() - startTime;
    console.log(`[Prefetch] Completed jurisdiction prefetch in ${totalTime}ms: ${Object.keys(newCorrections).length} of ${bullets.length} succeeded`);
    // Debug: Log the cached bullet keys for verification
    console.log("[Prefetch] Cached bullet keys:", Object.keys(newCorrections).map(k => k.substring(0, 60)));
  };

  // Pre-fetch jurisdiction details from structured issues (no API calls needed - data is already in issues)
  const prefetchJurisdictionDetailsFromIssues = async (issues: JurisdictionIssue[], _fullDoc: string, _currentDocType: DocType) => {
    if (issues.length === 0) return;

    setPrefetchingJurisdiction(true);
    console.log(`[Prefetch] Processing ${issues.length} jurisdiction issues (structured format - no API calls needed)`);

    // For structured issues, the correction is already available in the issue object
    // We just need to format it and store it with the right key
    const newCorrections: { [key: string]: string } = {};

    for (const issue of issues) {
      // Create a bullet key that matches how renderJurisdictionAnalysis will look it up
      const bulletKey = `• Anchor: ${issue.anchor}`;

      // Format the correction HTML similar to formatJurisdictionCorrection
      const formattedCorrection = `
<b>Analysis:</b> ${issue.analysis}

<b>Current text:</b> "${issue.currentText}"

<b>Correction:</b> "${issue.correction}"
      `.trim();

      newCorrections[bulletKey] = formattedCorrection;
    }

    setCorrections((prev: { [key: string]: string }) => ({ ...prev, ...newCorrections }));
    setPrefetchingJurisdiction(false);
    console.log(`[Prefetch] Completed jurisdiction prefetch from structured issues: ${issues.length} issues cached`);
    console.log("[Prefetch] Cached issue keys:", Object.keys(newCorrections).map(k => k.substring(0, 60)));
  };

  // Pre-fetch all clause check bullet details using batch endpoint (single request)
  const prefetchClauseDetails = async (analysisText: string, fullDoc: string, sessionId?: string) => {
    // IMPORTANT: Use same transformation as render (trim) to ensure cache keys match
    const bullets = analysisText.split("\n").map((line) => line.trim()).filter((line) => line.startsWith("•"));
    if (bullets.length === 0) return;

    setPrefetchingClause(true);
    console.log(`[Prefetch] Starting batch fetch for ${bullets.length} clause bullets`);

    const jurisdictionMap = JURISDICTION_CODES;
    const jurisdictionCode = jurisdictionMap[jurisdiction] || undefined;
    const documentType = uploadedTemplates.clause?.length ? "clause" : "protocol";

    // Extract anchors from all bullets
    const anchors = bullets.map(bullet => extractAnchorFromBullet(bullet) || extractBulletTitle(bullet));
    const bulletToAnchor = new Map(bullets.map((bullet, i) => [anchors[i], bullet]));

    try {
      // Use batch endpoint - single request for all anchors
      const startTime = Date.now();
      const response = await postClauseCheckRefineBatch({
        sessionId: sessionId || undefined,
        anchors,
        docContent: sessionId ? undefined : fullDoc, // Only send doc if no session
        jurisdiction: jurisdictionCode,
        clientName,
        projectNumber,
        documentType,
      });

      const newCorrections: { [key: string]: string } = {};
      let cachedCount = 0;

      for (const result of response.results) {
        if (result.analysis) {
          // Find the original bullet for this anchor
          const bullet = bulletToAnchor.get(result.anchor);
          if (bullet) {
            newCorrections[bullet] = formatClauseCorrection(result.analysis);
          }
          if (result.cached) cachedCount++;
        } else if (result.error) {
          console.warn(`[Prefetch] Failed for anchor "${result.anchor}":`, result.error);
        }
      }

      setClauseCorrections((prev: { [key: string]: string }) => ({ ...prev, ...newCorrections }));
      const elapsed = Date.now() - startTime;
      console.log(`[Prefetch] Batch completed in ${elapsed}ms: ${Object.keys(newCorrections).length}/${bullets.length} succeeded, ${cachedCount} from cache`);
      // Debug: Log the cache keys that were stored
      console.log("[Prefetch] Cached bullet keys:", Object.keys(newCorrections).map(k => k.substring(0, 60)));
    } catch (err) {
      console.error("[Prefetch] Batch request failed:", err);
    } finally {
      setPrefetchingClause(false);
    }
  };

  // Pre-fetch clause details from structured issues (no API calls needed - data is already in issues)
  const prefetchClauseDetailsFromIssues = (issues: ClauseIssue[]) => {
    if (issues.length === 0) return;

    setPrefetchingClause(true);
    console.log(`[Prefetch] Processing ${issues.length} clause issues (structured format - no API calls needed)`);

    // For structured issues, the correction is already available in the issue object
    // We just need to format it and store it with the right key
    const newCorrections: { [key: string]: string } = {};

    for (const issue of issues) {
      // Create a bullet key that matches how renderClauseCheck will look it up
      const bulletKey = `• Anchor: ${issue.anchor}`;

      // Format the correction HTML similar to formatClauseCorrection
      const formattedCorrection = `
<b>Paragraph:</b> ${issue.paragraph || "N/A"}

<b>Analysis:</b> ${issue.analysis}

<b>Current text:</b> "${issue.currentText}"

<b>Correction:</b> "${issue.correction}"
      `.trim();

      newCorrections[bulletKey] = formattedCorrection;
    }

    // Replace (not merge) to avoid stale corrections from previous runs
    setClauseCorrections(newCorrections);
    setPrefetchingClause(false);
    console.log(`[Prefetch] Completed clause prefetch from structured issues: ${issues.length} issues cached`);
    console.log("[Prefetch] Cached issue keys:", Object.keys(newCorrections).map(k => k.substring(0, 60)));
  };

  // Clear sessionStorage when the task pane is closed (user clicks X button)
  useEffect(() => {
    const handleUnload = () => {
      sessionStorage.removeItem("esi_corrections");
      sessionStorage.removeItem("esi_clause_corrections");
      sessionStorage.removeItem("esi_editor_flaws");
      sessionStorage.removeItem("esi_accepted_corrections");
    };
    window.addEventListener("beforeunload", handleUnload);
    return () => window.removeEventListener("beforeunload", handleUnload);
  }, []);

  // Track if Office.onReady has already been processed (prevents double initialization on React remount)
  const officeInitializedRef = useRef(false);

  // On add-in load, check doc content and set docType and docStatus
  useEffect(() => {
    // Set Office.initialize before onReady to prevent timing issues on slow networks
    // See: https://github.com/OfficeDev/office-js/issues/3330
    Office.initialize = () => {};

    Office.onReady(async (info) => {
      // Guard against multiple initializations (React strict mode, HMR, etc.)
      if (officeInitializedRef.current) {
        console.log("[Office] Already initialized, skipping");
        return;
      }
      officeInitializedRef.current = true;

      // Drop analysis state restored from sessionStorage if it belongs to a
      // different document than the one this pane just opened in.
      resetStateIfDocumentSwitched().catch(() => { /* best effort */ });

      if (info.host === Office.HostType.Word) {
        await queuedWordRun(async (context) => {
          const body = context.document.body;

          // Step 1: Load text first (most reliable operation)
          body.load("text");
          await context.sync();
          const content = body.text;
          console.log("[Office] Document text loaded, length:", content.length);

          // Use body.text for the initial snapshot — getHtml() frequently fails or returns
          // an empty skeleton and blocks Word for several seconds while trying, causing the
          // add-in to freeze on startup. All snapshots (startup, flow start, editor) now use
          // text consistently to avoid getHtml() freezes and format mismatches.
          setSnapshotAtPluginStart(content);
          console.log("[Office] Snapshot set from text, length:", content.length);
          const detectedType = detectDocTypeFromContent(content);
          if (content.trim().length === 0) {
            setDocStatus("blank");
            setDocType(null);
            setFlowStep("chooseType");
          } else if (detectedType) {
            setDocType(detectedType);
            setDocStatus("existing");
            setFlowStep("confirmExisting");
          } else {
            setDocStatus("blank");
            setDocType(null);
            setFlowStep("chooseType");
          }
        }).catch((error: unknown) => {
          // Log full error details including debugInfo for RichApi errors
          console.error("[Office] Error reading document:", error);
          if (error && typeof error === 'object' && 'debugInfo' in error) {
            const debugInfo = (error as { debugInfo: unknown }).debugInfo;
            console.error("[Office] Debug info:", JSON.stringify(debugInfo, null, 2));
            // Log specific debug fields if available
            if (debugInfo && typeof debugInfo === 'object') {
              const di = debugInfo as Record<string, unknown>;
              if (di.code) console.error("[Office] Error code:", di.code);
              if (di.message) console.error("[Office] Error message:", di.message);
              if (di.errorLocation) console.error("[Office] Error location:", di.errorLocation);
            }
          }
          // Don't re-throw - Word.run should release context even on error
          // Set a sensible default state so the app is still usable
          setDocStatus("blank");
          setDocType(null);
          setFlowStep("chooseType");
        });
      }
    });
  }, []);

  // If flowStep is jurisdictionAuto, detect the jurisdiction
  useEffect(() => {
    if (flowStep === "jurisdictionAuto") {
      // F4: Wrap async IIFE in try/catch for error handling
      (async () => {
        try {
          const detected = await detectJurisdictionFromContent();
          if (detected && detected.trim() !== "") {
            setDetectedJurisdiction(detected);
          } else {
            setFlowStep("jurisdictionManual");
          }
        } catch (e) {
          console.error("Failed to detect jurisdiction:", e);
          setFlowStep("jurisdictionManual");
        }
      })();
    }
  }, [flowStep]);

  // Fetch court links from Azure Blob
  useEffect(() => {
    getCourtLinks()
      .then((data) => setCourtLinks(data))
      .catch((err) => console.error("Error fetching court links:", err));
  }, []);

  // When entering Clause Check, run the analysis
  useEffect(() => {
    if (flowStep === "clauseCheck") {
      handleClauseCheckAnalysis();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flowStep]);

  // Back button
  const handleBack = () => {
    if (flowStep === "confirmExisting") {
      setFlowStep("chooseType");
    } else if (flowStep === "analyzing") {
      // Step back to client checklist before analyzing
      setFlowStep("clientChecklist");
    } else if (flowStep === "clientChecklist") {
      // Return to entry step
      if (docStatus === "existing") {
        setFlowStep("confirmExisting");
      } else {
        setFlowStep("chooseType");
      }
    } else if (flowStep === "jurisdictionAuto" || flowStep === "jurisdictionManual") {
      setFlowStep("confirmExisting");
    } else if (flowStep === "nextSteps") {
      setFlowStep("jurisdictionAuto");
    } else if (flowStep === "analysisComplete") {
      setFlowStep("nextSteps");
    } else if (flowStep === "clauseCheck") {
      setFlowStep("analysisComplete");
    } else if (flowStep === "esiFlowEditor") {
      setFlowStep("clauseCheck");
    } else if (flowStep === "talkingPoints") {
      setFlowStep("esiFlowEditor");
    }
  };

  // Detect jurisdiction from doc content
  const detectJurisdictionFromContent = async (): Promise<string> => {
    try {
      return await queuedWordRun(async (context) => {
        const body = context.document.body;
        context.load(body, "text");
        await context.sync();
        return detectJurisdictionInText(body.text);
      });
    } catch (error) {
      console.error("Error detecting jurisdiction:", error);
      return "";
    }
  };

  // Load a predefined template
  const handleLoadTemplate = async (type: DocType) => {
    selectedDocTypeRef.current = type;
    docTypeRef.current = type;
    setDocType(type);
    // Continue to jurisdiction selection for template loading
    setFlowStep("jurisdictionManual");
  };

  const handleManualJurisdictionSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    const selectedJurisdiction = formData.get("jurisdiction") as string;
    if (selectedJurisdiction) {
      setJurisdiction(selectedJurisdiction);
      setDetectedJurisdiction(""); // an explicit manual choice wins over auto-detection
      // Skip template loading for existing documents
      if (docStatus === "existing") {
        setFlowStep("nextSteps");
        return;
      }
      try {
        const data = await getTemplate(docType!, selectedJurisdiction);
        const templateContent = data.template || "No template found.";
        await queuedWordRun(async (context) => {
          const body = context.document.body;
          body.insertHtml(templateContent, Word.InsertLocation.start);
          await context.sync();
        });

        // immediately inspect the inserted text
        await queuedWordRun(async (ctx) => {
          const full = ctx.document.body;
          full.load("text");
          const htmlResult = full.getHtml();
          await ctx.sync();
          console.log("🔸 after insertHtml, doc.text is:", full.text);
          // For template-based drafts, treat the freshly inserted template as the
          // new baseline so Talking Points summarize the user's edits *after*
          // the template is loaded (rather than from an originally blank document).
          const html = htmlResult.value || "";
          setSnapshotAtPluginStart(html || full.text);
        });

        setFlowStep("clientChecklist");
      } catch (error) {
        console.error("Error loading template:", error);
        // F5: Safe error message extraction
        const message = error instanceof Error ? error.message : String(error);
        alert(`Error loading template: ${message}`);
      }
    }
  };

  const handleChooseProtocol = () => {
    selectedDocTypeRef.current = "protocol";
    docTypeRef.current = "protocol";
    setDocType("protocol");
    setFlowStep("clientChecklist");
  };

  const handleChooseProtective = () => {
    selectedDocTypeRef.current = "protective";
    docTypeRef.current = "protective";
    setDocType("protective");
    setFlowStep("clientChecklist");
  };

  const handleConfirmExistingYes = () => {
    // Keep detected docType; proceed to Analyzing first
    setFlowStep("clientChecklist");
  };

  const handleConfirmExistingNo = async () => {
    try {
      if (Office.addin && Office.addin.hide) {
        await Office.addin.hide();
      } else if (Office.context?.ui?.closeContainer) {
        Office.context.ui.closeContainer();
      } else {
        setFlowStep("closed");
      }
    } catch (error) {
      console.error("Error closing the add-in:", error);
      setFlowStep("closed");
    }
  };

  // Best-effort stable identity for the active document — its file URL. Used
  // only to detect a document switch; empty string means "unknown" (e.g. an
  // unsaved doc or a host that doesn't expose the URL), in which case we make
  // no assumptions and leave state untouched.
  const getDocumentIdentity = (): Promise<string> =>
    new Promise((resolve) => {
      try {
        Office.context.document.getFilePropertiesAsync((res) => {
          if (
            res.status === Office.AsyncResultStatus.Succeeded &&
            res.value &&
            typeof res.value.url === "string"
          ) {
            resolve(res.value.url);
          } else {
            resolve("");
          }
        });
      } catch {
        resolve("");
      }
    });

  // Clear every store that is scoped to a single document, so nothing from a
  // previously-analyzed draft leaks into the next one (stale flagged lists,
  // Editor flaws, accepted corrections, talking-points inputs, and — most
  // importantly — the backend clauseSessionId, whose reuse could make a refine
  // call fetch the PREVIOUS document's text).
  const clearPerDocumentState = () => {
    selectedDocTypeRef.current = null;
    docTypeRef.current = null;
    setScorecard(null);
    setBaselineScorecard(null);
    setAnalyzeError("");
    setAnalysis("");
    setJurisdictionRun(false);
    setEditorRun(false);
    injectionDecisionRef.current = null;
    setEditorFlaws([]);
    setAcceptedCorrections([]);
    setClauseSessionId(null);
    setClauseIssues([]);
    setClauseCheckComplete(false);
    setClauseAnalysis("");
    setClauseCorrections({});
    setCorrections({});
    setJurisdictionIssues([]);
    try {
      sessionStorage.removeItem("esi_corrections");
      sessionStorage.removeItem("esi_clause_corrections");
      sessionStorage.removeItem("esi_editor_flaws");
      sessionStorage.removeItem("esi_accepted_corrections");
    } catch {
      /* ignore */
    }
  };

  // Detect whether the active document changed since our last analysis and, if
  // so, clear the per-document state. Called at the start of each analysis.
  const resetStateIfDocumentSwitched = async () => {
    const identity = await getDocumentIdentity();
    if (!identity) return; // unknown identity — don't risk clobbering good state
    const previous = lastDocIdentityRef.current;
    lastDocIdentityRef.current = identity;
    // Persist alongside the sessionStorage-restored state, so state restored after
    // a pane reload in a DIFFERENT document is recognised as stale.
    try { sessionStorage.setItem("esi_doc_identity", identity); } catch { /* ignore */ }
    if (previous && previous !== identity) {
      console.log("[DocSwitch] Active document changed — clearing per-document analysis state");
      clearPerDocumentState();
    }
  };

  // Once the user chose to exclude flagged passages, keep them out of EVERY
  // later AI call on this document too (issue details, talking points).
  const applyInjectionDecision = (text: string): string => {
    const d = injectionDecisionRef.current;
    return d && d.decision === "exclude" ? excludeFindings(text, d.quotes) : text;
  };

  /**
   * Check the document for text written to steer an AI review before it is
   * analyzed. Resolves to the text to analyze (flagged passages removed if the
   * user chose so), or null if the user cancelled. Never blocks on its own
   * failure: if the check can't run, the analysis proceeds with a notice.
   */
  const guardDocument = (text: string): Promise<string | null> => {
    const hash = hashText(text);
    const decided = injectionDecisionRef.current;
    if (decided && decided.hash === hash) return Promise.resolve(applyInjectionDecision(text));
    const inflight = injectionInflightRef.current;
    if (inflight && inflight.hash === hash) return inflight.promise; // two analyses started together share one check

    const promise = (async (): Promise<string | null> => {
      let result: { findings: InjectionFinding[]; partial: boolean };
      setInjectionScanning(true);
      try {
        result = await postInjectionScan(text);
      } catch (err) {
        console.warn("[InjectionScan] check unavailable:", err);
        setInjectionNotice("The check for hidden instructions to AI couldn't run, so analysis will continue without this check. This does not mean the document is free of these instructions.");
        return text;
      } finally {
        setInjectionScanning(false);
      }
      setInjectionNotice(result.partial ? "Part of this document couldn't be checked for hidden instructions to AI." : "");
      if (result.findings.length === 0) {
        injectionDecisionRef.current = { hash, quotes: [], decision: "clean" };
        return text;
      }
      const choice = await new Promise<InjectionChoice>((resolve) =>
        setInjectionReview({ findings: result.findings, partial: result.partial, resolve })
      );
      setInjectionReview(null);
      if (choice === "cancel") return null;
      injectionDecisionRef.current = { hash, quotes: result.findings.map((f) => f.quote), decision: choice };
      return applyInjectionDecision(text);
    })();
    injectionInflightRef.current = { hash, promise };
    const release = () => {
      if (injectionInflightRef.current?.promise === promise) injectionInflightRef.current = null;
    };
    promise.then(release, release);
    return promise;
  };

  const showInjectionInDocument = async (quote: string) => {
    const probe = firstSearchableLine(quote);
    if (!probe) return;
    try {
      await queuedWordRun(async (context) => {
        const results = context.document.body.search(probe, { matchCase: false });
        results.load("items");
        await context.sync();
        if (results.items.length > 0) {
          results.items[0].select();
          await context.sync();
        }
      });
    } catch (err) {
      console.warn("[InjectionScan] could not locate passage in document:", err);
    }
  };

  // RAG-based jurisdiction analysis (synchronous)
  // A failed or cancelled run must not receive a successful-completion badge.
  const handleJurisdictionAnalysis = async (fullDoc?: string, overrideDocType?: DocType): Promise<boolean> => {
    setJurisdictionRun(false);
    try {
      // If the user switched to a different document since the last run, wipe
      // any carried-over per-document state before analyzing the new one.
      await resetStateIfDocumentSwitched();
      // Clear per-issue jurisdiction corrections cached from a prior document
      // state so a re-run reflects the current document (not stale results).
      setCorrections({});
      try { sessionStorage.removeItem("esi_corrections"); } catch { /* ignore */ }
      const jcTotalStart = performance.now();
      const rawContent = fullDoc || await getFullDocumentText();
      const content = await guardDocument(rawContent);
      if (content === null) return false; // user cancelled at the injection check
      console.log(`[JurisdictionCheck] Document read in ${((performance.now() - jcTotalStart) / 1000).toFixed(2)}s`);
      const typeToUse = overrideDocType || docType || "protocol";
      const actualJurisdiction =
        detectedJurisdiction?.trim().length > 0
          ? detectedJurisdiction
          : jurisdiction;
      const payload = {
        query: `Analyze if the jurisdiction ${actualJurisdiction} is appropriate for this document.`,
        docContent: content,
        detectedJurisdiction: actualJurisdiction,
        docType: typeToUse,
      };

      // Set loading state with placeholder to show loading indicator
      setAnalysis("__LOADING__"); // Special marker to show loading state
      setJurisdictionIssues([]); // Clear previous issues
      setFlowStep("analysisComplete"); // Show results panel

      // Use synchronous call (no streaming)
      console.log("[JurisdictionCheck] Starting analysis for:", actualJurisdiction);
      const jcStart = performance.now();
      const data = await postJurisdictionCheck(payload);
      const jcEnd = performance.now();
      console.log(`[JurisdictionCheck] API completed in ${((jcEnd - jcStart) / 1000).toFixed(2)}s`);
      console.log("[JurisdictionCheck] Analysis received, length:", (data.analysis || "").length);
      console.log("[JurisdictionCheck] Structured issues count:", (data.issues || []).length);

      // Use structured issues array (more stable than text parsing)
      const issues = data.issues || [];
      setJurisdictionIssues(issues);
      setAnalysis(data.analysis || "");

      // Mark that Jurisdiction Check has been run so the top bar can show badge state
      setJurisdictionRun(true);
      // Pre-fetch all issue details in parallel for instant click response
      prefetchJurisdictionDetailsFromIssues(issues, content, typeToUse);
      console.log(`[JurisdictionCheck] Total time: ${((performance.now() - jcTotalStart) / 1000).toFixed(2)}s`);
      return true;
    } catch (err) {
      console.error("[JurisdictionCheck] Error during analysis:", err);
      setJurisdictionIssues([]);
      setAnalysis(`Error: ${err instanceof Error ? err.message : "Failed to analyze jurisdiction. Please try again."}`);
      return false;
    } finally {
      setIsLoading(false); // Ensure loading state is reset
    }
  };

  // Jurisdiction Summary state
  const [jurisdictionSummary, setJurisdictionSummary] = useState<string | null>(null);
  const [jurisdictionSummaryLoading, setJurisdictionSummaryLoading] = useState(false);

  useEffect(() => {
    // When the Jurisdiction Check page is shown, fetch summary from backend
    const loadSummary = async () => {
      try {
        setJurisdictionSummaryLoading(true);
        const docText = await getFullDocumentText();
        const actualLabel =
          (detectedJurisdiction && detectedJurisdiction.trim().length > 0)
            ? detectedJurisdiction
            : (jurisdiction || "");
        const resp = await apiFetch(`${API_BASE}/api/jurisdiction/summary`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            docContent: docText,
            docType: docType || "protocol",
            label: actualLabel
          }),
        });
        if (resp.ok) {
          const data = await resp.json();
          setJurisdictionSummary((data && data.content) || "");
        } else {
          setJurisdictionSummary("");
        }
      } catch (e) {
        setJurisdictionSummary("");
      } finally {
        setJurisdictionSummaryLoading(false);
      }
    };

    if (flowStep === "analysisComplete") {
      loadSummary();
    }
    // F3: Include all dependencies used in loadSummary
  }, [flowStep, docType, detectedJurisdiction, jurisdiction]);

  const handleJurisdictionConfirm = async () => {
    setIsLoading(true); // Start loading
    try {
      setJurisdiction(detectedJurisdiction);
      await handleJurisdictionAnalysis();
    } finally {
      setIsLoading(false); // Stop loading
    }
  };

  const handleJurisdictionReject = () => {
    // The detected district was wrong — drop it, or analysis (which prefers the
    // detected value) would silently keep using it over the user's manual pick.
    setDetectedJurisdiction("");
    setFlowStep("jurisdictionManual");
  };

  // Extract section number from bullet text
  const extractSectionNumber = (bullet: string): number | null => {
    // Try "Section X" pattern first
    const matchSection = bullet.match(/Section\s+(\d+(?:\.\d+)?)/i);
    if (matchSection) {
      return parseFloat(matchSection[1]);
    }

    // Then try a leading number (e.g., "1: Heading" or "2. Some text")
    const matchLeading = bullet.match(/^\s*•?\s*(\d+(?:\.\d+)*)(?::|\.)/);
    if (matchLeading) {
      return parseFloat(matchLeading[1]);
    }

    return null;
  };

  // Helper to extract all 'Current text:' blocks from a correction string
  const extractAllCurrentTexts = (correction: string): string[] => {
    // Strip HTML tags that may wrap the headings (e.g., <strong>)
    const plain = correction.replace(/<[^>]+>/g, "");
    const regex = /Current text:\s*"([\s\S]*?)"/g;
    const matches: string[] = [];
    let match;
    while ((match = regex.exec(plain)) !== null) {
      if (match[1] && match[1].trim().length > 0 && match[1].trim().toLowerCase() !== "no existing text") {
        matches.push(match[1].trim());
      }
    }
    return matches;
  };

  // Helper to extract the 'Paragraph:' snippet for highlighting
  const extractParagraphSnippet = (correction: string): string | null => {
    // Strip HTML tags that may wrap the headings (e.g., <strong>)
    const plain = correction.replace(/<[^>]+>/g, "");
    const lower = plain.toLowerCase();
    const label = "paragraph:";
    const idx = lower.indexOf(label);
    if (idx === -1) return null;

    // Text immediately after "Paragraph:"
    let after = plain.slice(idx + label.length).trim();
    if (!after) return null;

    // Case 1: Quoted value → take content inside first pair of quotes
    if (after[0] === '"' || after[0] === "'" || after[0] === "\u201c" || after[0] === "\u2018") {
      const quoteChar = after[0];
      const endIdx = after.indexOf(quoteChar, 1);
      if (endIdx > 1) {
        const inner = after.slice(1, endIdx).trim();
        return inner.length > 0 ? inner : null;
      }
      // Fallback: drop opening quote and continue
      after = after.slice(1).trim();
    }

    // Case 2: Unquoted value → take up to end of line or before next heading label
    const boundaryMatch = after.match(/\r?\n|Analysis:|Current text:|Recommended Edits:|Correction:/i);
    const end = boundaryMatch && boundaryMatch.index !== undefined ? boundaryMatch.index : after.length;
    const snippet = after.slice(0, end).trim();

    // Strip any trailing punctuation/quotes
    const cleaned = snippet.replace(/^["'"']+/, "").replace(/["'"']+$/, "").trim();
    return cleaned.length > 0 ? cleaned : null;
  };

  // OPTIMIZED: Batch highlighting function - collects all texts first, then highlights in single Word.run()
  // This prevents flickering by reducing multiple document refreshes to just one
  const highlightAllCurrentTextsOptimized = async (
    sectionNumber: number,
    correction: string
  ): Promise<boolean> => {
    // New behavior: use the Paragraph heading/label as the primary anchor for highlighting
    const paragraphSnippet = extractParagraphSnippet(correction);

    if (!paragraphSnippet) {
      // If we don't have a usable Paragraph snippet, do not apply any highlight
      console.warn("⚠️ No Paragraph snippet available for highlighting");
      return false;
    }

    // Collect ALL texts to search for in priority order (most specific first),
    // starting from the Paragraph snippet
    const textsToSearch: string[] = [];

    const snippet = paragraphSnippet.trim();
    if (!snippet) {
      console.warn("⚠️ Paragraph snippet is empty after trimming – skipping highlight");
      return false;
    }

    // Priority 1: Full snippet (highest priority)
    textsToSearch.push(snippet);

    // Priority 2: Sentences from snippet
    const sentences = snippet
      .split(/(?<=\.)\s+/)
      .map((s) => s.trim())
      .filter((s) => s.length > 8);
    textsToSearch.push(...sentences);

    // Priority 3: Title and content parts (if colon exists)
    if (snippet.includes(":")) {
      const [title, ...rest] = snippet.split(":");
      const titlePart = title.trim();
      const contentPart = rest.join(":").trim();
      if (titlePart.length > 5) textsToSearch.push(titlePart);
      if (contentPart.length > 8) textsToSearch.push(contentPart);
    }

    // Priority 4: Common variations (only top 2 most likely)
    textsToSearch.push(
      snippet.replace(/\*/g, "").replace(/•/g, "").trim(),
      snippet.replace(/\r?\n/g, " ").replace(/\s+/g, " ").trim()
    );

    // Priority 5: Bullet parts (only if snippet has multiple lines)
    if (snippet.includes("\n")) {
      const bulletParts = snippet
        .split(/\r?\n/)
        .map((l) => l.replace(/^\s*[\*•]\s*/, "").trim())
        .filter((l) => l.length > 8);
      textsToSearch.push(...bulletParts);
    }

    // Remove duplicates and empty strings
    const uniqueTexts = Array.from(new Set(textsToSearch.filter(t => t && t.length >= 4)));

    console.log(`[Optimized Highlighting] Searching for ${uniqueTexts.length} unique text patterns in single batch`);

    // Perform ALL searches in a SINGLE Word.run() context to prevent flickering
    const found = await highlightMultipleTextsBatch(sectionNumber, uniqueTexts);
    if (!found) {
      console.warn("⚠️ No text snippet matched for Paragraph-based highlighting");
    }
    return found;
  };

  // Helper to highlight all Current text snippets with the same advanced logic
  // DEPRECATED: Use highlightAllCurrentTextsOptimized instead to prevent flickering
  const highlightAllCurrentTexts = async (
    sectionNumber: number,
    correction: string
  ): Promise<boolean> => {
    // Use optimized version to prevent flickering
    return highlightAllCurrentTextsOptimized(sectionNumber, correction);
  };

  // Clear highlights using a fresh Word.run context (avoids cross-context proxy issues).
  // Clears yellow highlights set by this plugin.
  // PERF: Uses 2 context.sync() calls instead of 3 by loading font/highlightColor
  // in the initial paragraph load, avoiding a separate property-load round-trip.
  const clearAllHighlights = async () => {
    // Fast path: nothing to clear
    if (highlightedParagraphsRef.current.length === 0) {
      setHighlightedSection(null);
      return;
    }

    await queuedWordRun(async (context) => {
      const body = context.document.body;
      const paragraphs = body.paragraphs;
      paragraphs.load("font/highlightColor");
      await context.sync();

      for (const para of paragraphs.items) {
        if (para.font.highlightColor === '#FFFF00') {
          para.font.highlightColor = null;
        }
      }
      await context.sync();
    });

    // Clear the tracking array
    highlightedParagraphsRef.current = [];
    setHighlightedSection(null);
  };

  // Update: Clear all highlights before applying new highlights for a requirement
  const handleJurisdictionFlawClick = async (index: number, bullet: string) => {
    if (selectedBulletIndex === index) {
      setSelectedBulletIndex(null);
      return;
    }
    setSelectedBulletIndex(index);

    // Calculate section number first
    let effectiveSection = extractSectionNumber(bullet);
    if (effectiveSection === null) {
      effectiveSection = index + 1;
    }

    // Check if we already have pre-fetched/cached data for this bullet BEFORE clearing content
    // First check React state, then fallback to sessionStorage (handles race condition)
    let cachedCorrection = corrections[bullet];
    if (!cachedCorrection) {
      // Fallback: check sessionStorage directly in case state hasn't updated yet
      try {
        const stored = sessionStorage.getItem('esi_corrections');
        if (stored) {
          const parsed = JSON.parse(stored);
          cachedCorrection = parsed[bullet];
          if (cachedCorrection) {
            console.log("[Cache] Found correction in sessionStorage fallback");
          }
        }
      } catch {
        // Ignore parse errors
      }
    }

    if (cachedCorrection) {
      console.log("[Cache] Using cached correction for bullet:", bullet.substring(0, 50));
      // Set content immediately from cache - no loading spinner needed
      setSelectedBulletContent(cachedCorrection);
      // Clear old highlights and apply new ones
      await clearAllHighlights();
      console.log("🔍 Starting optimized batch highlighting (cached)...");
      const foundAny = await highlightAllCurrentTextsOptimized(effectiveSection, cachedCorrection);
      if (!foundAny) {
        console.warn("⚠️ No text snippet matched exactly - keeping section highlight as fallback");
        await highlightSection(effectiveSection);
      }
      return;
    }

    // No cache hit - show loading spinner and fetch from API
    console.log("[Cache] No cached correction found, fetching from API:", bullet.substring(0, 50));
    setSelectedBulletContent("");

    // 1) Clear any old highlights
    await clearAllHighlights();

    // 2) Fetch the AI correction (fallback if not pre-fetched)
    const anchor = extractAnchorFromBullet(bullet) || extractBulletTitle(bullet);
    const fullDoc = applyInjectionDecision(await getFullDocumentText());

    // Re-detect docType from current document content
    const detectedType = resolveDocumentType(fullDoc, selectedDocTypeRef.current);
    setDocType(detectedType);
    docTypeRef.current = detectedType;
    console.log("[DEBUG] docType at flaw click (after re-detect):", detectedType);

    try {
      const data = await postFlawAnalysis({
        docContent: fullDoc,
        requirement: anchor,
        jurisdiction: activeJurisdictionCode(),
        clientName,
        documentType: detectedType,
      });

      // 3) Clean up the AI block headings…
      let formattedCorrection = data.analysis
        .trim()
        .replace(/^•\s*Anchor:[^\n]*\n?/i, "")
        .replace(/(^|\n)\s*•?\s*Paragraph:/gi, "$1<strong>Paragraph:</strong>")
        .replace(/(^|\n)\s*•?\s*Analysis:/gi, "$1<strong>Analysis:</strong>")
        .replace(/(^|\n)\s*•?\s*Current text:/gi, "$1<strong>Current text:</strong>")
        .replace(
          /(^|\n)\s*•?\s*(Correction|Recommended Edits):/gi,
          "$1<strong>Recommended Edits:</strong>"
        );

      // Add a horizontal rule before each additional Analysis block when multiple corrections exist
      let firstJurAnalysis = true;
      formattedCorrection = formattedCorrection.replace(/<strong>Analysis:<\/strong>/gi, (match) => {
        if (firstJurAnalysis) {
          firstJurAnalysis = false;
          return match;
        }
        return `<hr style="margin:8px 0;border-top:1px solid #e5e7eb;" />\n${match}`;
      });

      // 4) Keep Recommended Edits in plain black text (no bold, no italic, no green)
      // Extract and preserve the Recommended Edits text as plain text
      const corrMatch = formattedCorrection.match(
        /<strong>(?:Recommended Edits|Correction):<\/strong>\s*"([^"]*)"/i
      );
      if (corrMatch) {
        // Use plain text without any formatting (no bold, no green, no italic)
        const correctionText = corrMatch[1];
        formattedCorrection = formattedCorrection.replace(
          /(<strong>(?:Recommended Edits|Correction):<\/strong>\s*")([^"]*)(")/i,
          `$1${correctionText}$3`
        );
      }
      // Strip any remaining formatting from Recommended Edits
      formattedCorrection = stripRecommendedEditsFormatting(formattedCorrection);
      setCorrections(prev => ({ ...prev, [bullet]: formattedCorrection }));
      setSelectedBulletContent(formattedCorrection);

      // 4) OPTIMIZED: Use batch highlighting to prevent flickering
      // All searches happen in a single Word.run() context instead of multiple sequential calls
      console.log("🔍 Starting optimized batch highlighting...");
      const foundAny = await highlightAllCurrentTextsOptimized(effectiveSection, formattedCorrection);

      // 5) Final fallback - if no specific text matched, ensure section is highlighted
      if (!foundAny) {
        console.warn("⚠️ No text snippet matched exactly - keeping section highlight as fallback");
        await highlightSection(effectiveSection);
      } else {
        console.log("✅ Successfully highlighted specific text content (optimized batch)");
      }

    } catch (error) {
      console.error("Error in handleJurisdictionFlawClick:", error);
      // F8: Provide user feedback on error
      const message = error instanceof Error ? error.message : String(error);
      setSelectedBulletContent(`<span style="color: red;">Error loading analysis: ${message}. Please try again.</span>`);
    }
  };



  // --- Accept a Jurisdiction Flaw and move to ESI Flow Editor ---
  const handleCorrectBulletForEditor = async (index: number, bullet: string) => {
    let sectionNumber = extractSectionNumber(bullet);
    if (sectionNumber === null) {
      sectionNumber = index + 1;
      console.warn("Section number not found in bullet, using index as fallback:", bullet);
    }
    const finalCorrection = corrections[bullet];
    if (!finalCorrection) {
      console.error("No correction content available for insertion.");
      return;
    }
    // Remove strict format check: accept any AI content

    // Add to ESI Flow Editor (editorFlaws)
    setEditorFlaws((prev) => [
      ...prev,
      { bullet, correction: finalCorrection, sectionNumber, source: "jurisdiction" },
    ]);

    // Remove from jurisdictionIssues array (this is what the UI renders)
    setJurisdictionIssues((prev) => {
      const updated = prev.filter((_, i) => i !== index);
      console.log(`[JurisdictionCheck] Accepted issue at index ${index}, remaining: ${updated.length}`);
      return updated;
    });

    // Also remove from legacy analysis string (for backwards compatibility)
    const bullets = (analysis || "")
      .split("\n")
      .map((line) => line.trim())
      .filter((line) => line.startsWith("•"));
    const updatedBullets = bullets.filter((_, i) => i !== index);
    setAnalysis(updatedBullets.join("\n"));

    setCorrections((prev) => {
      const newCorrections = { ...prev };
      delete newCorrections[bullet];
      return newCorrections;
    });
    setSelectedBulletIndex(null);
    setSelectedBulletContent("");
    await clearAllHighlights();
  };

  const handleIgnoreBulletForEditor = async (index: number, bullet: string) => {
    // Remove from jurisdictionIssues array (this is what the UI renders)
    setJurisdictionIssues((prev) => {
      const updated = prev.filter((_, i) => i !== index);
      console.log(`[JurisdictionCheck] Ignored issue at index ${index}, remaining: ${updated.length}`);
      return updated;
    });

    // Also remove from legacy analysis string (for backwards compatibility)
    const bullets = (analysis || "")
      .split("\n")
      .map((line) => line.trim())
      .filter((line) => line.startsWith("•"));
    const updatedBullets = bullets.filter((_, i) => i !== index);
    setAnalysis(updatedBullets.join("\n"));

    setCorrections((prev) => {
      const newCorrections = { ...prev };
      delete newCorrections[bullet];
      return newCorrections;
    });
    setSelectedBulletIndex(null);
    setSelectedBulletContent("");
    await clearAllHighlights();
  };

  // Clause Check bullet selection
  const handleClauseMissingRequirementClick = async (index: number, bullet: string) => {
    if (clauseSelectedBulletIndex === index) {
      setClauseSelectedBulletIndex(null);
      return;
    }
    setClauseSelectedBulletIndex(index);

    // Calculate section number first
    let effectiveSection = extractSectionNumber(bullet);
    if (effectiveSection === null) {
      effectiveSection = index + 1;
    }

    // Check if we already have pre-fetched/cached data for this bullet BEFORE clearing content
    // First check React state, then fallback to sessionStorage (handles race condition)
    let cachedCorrection = clauseCorrections[bullet];
    if (!cachedCorrection) {
      // Fallback: check sessionStorage directly in case state hasn't updated yet
      try {
        const stored = sessionStorage.getItem('esi_clause_corrections');
        if (stored) {
          const parsed = JSON.parse(stored);
          cachedCorrection = parsed[bullet];
          if (cachedCorrection) {
            console.log("[Cache] Found clause correction in sessionStorage fallback");
          }
        }
      } catch {
        // Ignore parse errors
      }
    }

    if (cachedCorrection) {
      console.log("[Cache] Using cached clause correction for bullet:", bullet.substring(0, 50));
      // Set content immediately from cache - no loading spinner needed
      setClauseSelectedBulletContent(cachedCorrection);
      // Clear old highlights and apply new ones
      await clearAllHighlights();
      console.log("🔍 Starting optimized batch highlighting (cached)...");
      const foundAny = await highlightAllCurrentTextsOptimized(effectiveSection, cachedCorrection);
      if (!foundAny) {
        console.warn("⚠️ No text snippet matched exactly - keeping section highlight as fallback");
        await highlightSection(effectiveSection);
      }
      return;
    }

    // No cache hit - show loading spinner and fetch from API
    console.log("[Cache] No cached clause correction found, fetching from API:", bullet.substring(0, 60));
    console.log("[Cache] Available clauseCorrections keys:", Object.keys(clauseCorrections).map(k => k.substring(0, 60)));
    setClauseSelectedBulletContent("");

    // Clear previous highlights
    await clearAllHighlights();

    // Extract the anchor or requirement from the bullet
    const anchor = extractAnchorFromBullet(bullet) || extractBulletTitle(bullet);
    // Always send full document content
    const fullDoc = applyInjectionDecision(await getFullDocumentText());
    const jurisdictionMap = JURISDICTION_CODES;
    const jurisdictionCode = jurisdictionMap[jurisdiction] || null;
    try {
      // Determine which documentType to use based on uploaded templates
      const documentType = uploadedTemplates.clause?.length ? "clause" : "protocol";
      const data = await postClauseCheckRefine({
        docContent: fullDoc,
        anchor,
        jurisdiction: jurisdictionCode || undefined,
        clientName,
        projectNumber,
        documentType,
      });
      let formattedCorrection = data.analysis.trim();
      // Remove any AI-generated source citations in parentheses or brackets, e.g.
      // "(Source: Requirement Details – Sample Clause; ESI Protocol Reference – Optional Clauses: Hyperlinks)"
      formattedCorrection = formattedCorrection
        .replace(/\s*\(Source:[^)]*\)/gi, "")
        .replace(/\s*\[Source:[^\]]*\]/gi, "");
      // Remove any lines that begin with 'Anchor:' (or '• Anchor:') so anchor labels are not shown in subsections
      formattedCorrection = formattedCorrection.replace(/(^|\n)\s*•?\s*Anchor:[^\n]*/gi, '').trim();

      // === Bold the AI block headings ===
      formattedCorrection = formattedCorrection
        .replace(/(^|\n)\s*•?\s*Paragraph:/gi, "$1<strong>Paragraph:</strong>")
        .replace(/(^|\n)\s*•?\s*Analysis:/gi, "$1<strong>Analysis:</strong>")
        .replace(/(^|\n)\s*•?\s*Current text:/gi, "$1<strong>Current text:</strong>")
        .replace(
          /(^|\n)\s*•?\s*(Correction|Recommended Edits):/gi,
          "$1<strong>Recommended Edits:</strong>"
        );

      // === Add a horizontal rule before each additional Analysis block (when multiple corrections exist) ===
      let isFirstAnalysis = true;
      formattedCorrection = formattedCorrection.replace(/<strong>Analysis:<\/strong>/gi, (match) => {
        if (isFirstAnalysis) {
          isFirstAnalysis = false;
          return match;
        }
        return `<hr style="margin:8px 0;border-top:1px solid #e5e7eb;" />\n${match}`;
      });

      // === Keep Recommended Edits in plain black text (no bold, no italic, no green) ===
      // Extract and preserve the Recommended Edits text as plain text
      const clauseCorr = formattedCorrection.match(
        /<strong>(?:Recommended Edits|Correction):<\/strong>\s*"([^"]*)"/i
      );
      if (clauseCorr) {
        // Use plain text without any formatting (no bold, no green, no italic)
        const correctionText = clauseCorr[1];
        formattedCorrection = formattedCorrection.replace(
          /(<strong>(?:Recommended Edits|Correction):<\/strong>\s*")([^"]*)(")/i,
          `$1${correctionText}$3`
        );
      }

      // Normalize heading: sometimes the AI writes "Document content:" instead of "Current text:"
      formattedCorrection = formattedCorrection.replace(/(^|\n)\s*Document content:/gi, '$1Current text:');
      // Strip any remaining formatting from Recommended Edits
      formattedCorrection = stripRecommendedEditsFormatting(formattedCorrection);
      setClauseCorrections((prev) => ({ ...prev, [bullet]: formattedCorrection }));
      setClauseSelectedBulletContent(formattedCorrection);
      // After receiving the AI correction, highlight using the Paragraph-based logic
      // (same behavior as Jurisdiction Check). No legacy section-wide fallback.
      // Note: clearAllHighlights() already called before API fetch (line above) - no need to repeat
      console.log("🔍 Starting optimized Paragraph-based highlighting (Clause Check)...");
      const foundAny = await highlightAllCurrentTextsOptimized(effectiveSection, formattedCorrection);
      if (!foundAny) {
        console.warn("⚠️ No text snippet matched for Clause Check Paragraph-based highlighting");
      } else {
        console.log("✅ Successfully highlighted Paragraph-based content (Clause Check - optimized batch)");
      }
    } catch (error) {
      console.error("Error fetching clause correction:", error);
      setClauseSelectedBulletContent("Error fetching correction.");
    }
  };

  // --- Accept a Clause Flaw and move to ESI Flow Editor ---
  const handleClauseCorrectBulletForEditor = async (index: number, bullet: string) => {
    let sectionNumber = extractSectionNumber(bullet);
    if (sectionNumber === null) {
      sectionNumber = index + 1;
      console.warn("Section number not found in bullet, using index as fallback:", bullet);
    }
    const finalCorrection = clauseCorrections[bullet];
    if (!finalCorrection) {
      console.error("No correction content available for insertion.");
      return;
    }
    // Remove strict format check: accept any AI content

    // Add to ESI Flow Editor (editorFlaws)
    setEditorFlaws((prev) => [
      ...prev,
      { bullet, correction: finalCorrection, sectionNumber, source: "clause" },
    ]);

    // Remove from clauseIssues array (this is what the UI renders)
    setClauseIssues((prev) => {
      const updated = prev.filter((_, i) => i !== index);
      console.log(`[ClauseCheck] Accepted issue at index ${index}, remaining: ${updated.length}`);
      return updated;
    });

    // Remove from legacy text analysis (for backwards compatibility)
    const bullets = (clauseAnalysis || "")
      .split("\n")
      .map((line) => line.trim())
      .filter((line) => line.startsWith("•"));
    const updatedBullets = bullets.filter((_, i) => i !== index);
    setClauseAnalysis(updatedBullets.join("\n"));

    setClauseCorrections((prev) => {
      const newCorrections = { ...prev };
      delete newCorrections[bullet];
      return newCorrections;
    });
    setClauseSelectedBulletIndex(null);
    setClauseSelectedBulletContent("");
    await clearAllHighlights();
  };

  const handleClauseIgnoreBullet = async (index: number, bullet: string) => {
    // Remove from clauseIssues array (this is what the UI renders)
    setClauseIssues((prev) => {
      const updated = prev.filter((_, i) => i !== index);
      console.log(`[ClauseCheck] Ignored issue at index ${index}, remaining: ${updated.length}`);
      return updated;
    });

    // Remove from legacy text analysis (for backwards compatibility)
    const bullets = (clauseAnalysis || "")
      .split("\n")
      .map((line) => line.trim())
      .filter((line) => line.startsWith("•"));
    const updatedBullets = bullets.filter((_, i) => i !== index);
    setClauseAnalysis(updatedBullets.join("\n"));
    setClauseCorrections((prev) => {
      const newCorrections = { ...prev };
      delete newCorrections[bullet];
      return newCorrections;
    });
    setClauseSelectedBulletIndex(null);
    setClauseSelectedBulletContent("");
    await clearAllHighlights();
  };

  // ESI Flow Editor
  const handleSuggestionClick = async (flaw: EditorFlaw, index: number) => {
    // Remove all previous highlights before applying new ones
    await clearAllHighlights();
    setExpandedFlawIndex(index);
    await highlightAllCurrentTexts(flaw.sectionNumber, flaw.correction);
  };

  const handleEditorAccept = async (flaw: EditorFlaw, index: number) => {
    // Extract the section title for display in the accepted corrections list
    const bulletWithoutSection = flaw.bullet
      .replace(/Section\s+\d+(?:\([a-zA-Z]\))?:?\s*/i, "")
      .replace(/^•\s*/, "")
      .replace(/^\.\d+:\s*/i, "")
      .replace(/Section\s+\d+(\.\d+)?(\([a-z]\))?:\s*/gi, "")
      .replace(/Anchor:\s*/i, "");
    const heading = `${bulletWithoutSection}`;

    // Extract the correction text for the details
    const correctionMatch = flaw.correction.match(
      /(?:<strong>)?(?:Recommended Edits|Correction):(?:<\/strong>)?\s*"([^"]*)"/i
    );
    const formattedCorrection = correctionMatch
      ? correctionMatch[1]
      : flaw.correction.replace(/Section\s+\d+:\s*/g, "");

    setAcceptedCorrections((prev) => [
      ...prev,
      { sectionNumber: flaw.sectionNumber, heading, details: formattedCorrection },
    ]);
    setEditorFlaws((prev) => prev.filter((_, i) => i !== index));
    setExpandedFlawIndex(null);
    setHighlightedSection(null);
  };

  const handleEditorIgnore = (index: number) => {
    setEditorFlaws((prev) => prev.filter((_, i) => i !== index));
    setExpandedFlawIndex(null);
  };

  const [isLoading, setIsLoading] = useState<boolean>(false);
  // AI Flaw Analysis
  const handleFlawAnalysis = async () => {
    // F7: Prevent race conditions from rapid clicks
    if (isLoading) {
      console.warn("[handleFlawAnalysis] Already loading, ignoring duplicate click");
      return;
    }
    setIsLoading(true);
    try {
      await resetStateIfDocumentSwitched();
      const fullDoc = await getFullDocumentText();
      // Re-detect docType from current document content
      const detectedType = resolveDocumentType(fullDoc, selectedDocTypeRef.current);
      setDocType(detectedType);
      docTypeRef.current = detectedType;
      console.log("[DEBUG] docType at analysis (after re-detect):", detectedType);
      if (!detectedType) {
        setDocStatus("blank");
        setFlowStep("chooseType");
        setIsLoading(false);
        return;
      }
      const ran = await handleJurisdictionAnalysis(fullDoc, detectedType);
      if (ran !== false) setJurisdictionRun(true);
    } finally {
      setIsLoading(false);
    }
  };

  // Clause Check Analysis
  const handleClauseCheckAnalysis = async () => {
    try {
      // If the user switched to a different document since the last run, wipe
      // any carried-over per-document state before analyzing the new one.
      await resetStateIfDocumentSwitched();
      setClauseRunStatus("ok");
      setClauseCheckComplete(false);
      // A fresh analysis run must not reuse per-issue corrections cached from a
      // previous document state. Clear them so each issue re-fetches against the
      // current document (fixes stale results after the user edits the document).
      setClauseCorrections({});
      try { sessionStorage.removeItem("esi_clause_corrections"); } catch { /* ignore */ }
      const ccTotalStart = performance.now();
      const rawContent = await getFullDocumentText();
      const content = await guardDocument(rawContent);
      if (content === null) {
        // user cancelled at the injection check
        setClauseIssues([]);
        setClauseRunStatus("cancelled");
        setClauseCheckComplete(true);
        return;
      }
      const ccDocReadEnd = performance.now();
      console.log(`[ClauseCheck] Document read in ${((ccDocReadEnd - ccTotalStart) / 1000).toFixed(2)}s`);
      const currentDocType = docTypeRef.current;
      console.log("[DEBUG] docType at clause check:", currentDocType);
      if (!currentDocType) {
        setDocStatus("blank");
        setFlowStep("chooseType");
        return;
      }
      // Map jurisdiction name to code
      const jurisdictionMap = JURISDICTION_CODES;
      const jurisdictionCode = jurisdictionMap[jurisdiction] || null;
      const documentType = currentDocType;
      const ccApiStart = performance.now();
      const data = await postClauseCheck({
        docContent: content,
        docType: documentType,
        jurisdiction: jurisdictionCode,
        clientName,
        projectNumber,
        documentType,
      });
      const ccApiEnd = performance.now();
      console.log(`[ClauseCheck] API completed in ${((ccApiEnd - ccApiStart) / 1000).toFixed(2)}s`);

      // Use structured issues array (more stable than text parsing)
      const issues = data.issues || [];
      setClauseIssues(issues);
      setClauseAnalysis(data.analysis || "");
      setClauseCheckComplete(true);
      console.log("[ClauseCheck] Structured issues count:", issues.length);
      console.log(`[ClauseCheck] Total time: ${((performance.now() - ccTotalStart) / 1000).toFixed(2)}s`);

      // Store sessionId for batch prefetch (avoids re-sending full document)
      const sessionId = data.sessionId;
      if (sessionId) {
        setClauseSessionId(sessionId);
        console.log("[ClauseCheck] Session created:", sessionId);
      }

      // Pre-fetch all issue details from structured data (no API calls needed)
      console.log("[ClauseCheck] Starting prefetch with issues count:", issues.length);
      prefetchClauseDetailsFromIssues(issues);
    } catch (error) {
      console.error("Error during clause check analysis:", error);
      setClauseAnalysis("Clause check failed. Please try again.");
      setClauseIssues([]);
      setClauseRunStatus("failed"); // never let a failure read as "all clauses addressed"
      setClauseCheckComplete(true);
    }
  };

  // AI-based summary for Talking Points
  // Simple approach: Compare initial document snapshot vs current document
  const generateTalkingPointsSummary = async () => {
    setTalkingPointsLoading(true);
    try {
      // Get initial document snapshot (captured when plugin started or flow began)
      const initialDocument =
        snapshotAtPluginStart || snapshotAtFlowStart || snapshotBeforeEditor || "";

      // Get current document from Word (use text to match initial snapshot format)
      let currentDocument = "";
      try {
        currentDocument = applyInjectionDecision(await getFullDocumentText());
      } catch (docError) {
        console.error("[TalkingPoints] Failed to get current document:", docError);
        throw new Error(`Failed to read document: ${docError instanceof Error ? docError.message : String(docError)}`);
      }

      // Debug logging
      console.log("[TalkingPoints] Document comparison:", {
        initialLength: initialDocument.length,
        currentLength: currentDocument.length,
        hasInitial: initialDocument.length > 0,
        hasCurrent: currentDocument.length > 0,
      });

      // Guard: detect empty HTML skeleton in current document and retry with plain text
      const currentTextContent = currentDocument.replace(/<[^>]*>/g, "").trim();
      if (currentDocument.length > 0 && currentTextContent.length === 0
          && initialDocument.replace(/<[^>]*>/g, "").trim().length > 0) {
        console.warn("[TalkingPoints] Current document is empty HTML skeleton, retrying with text");
        try {
          currentDocument = applyInjectionDecision(await getFullDocumentText());
        } catch {
          setTalkingPointsSummary(
            "Unable to generate talking points: Could not read the current document content. " +
            "Please click inside the document to activate it, then try again."
          );
          return;
        }
      }

      // Validate we have both versions to compare
      if (!initialDocument) {
        setTalkingPointsSummary(
          "Unable to generate talking points: No baseline document snapshot was captured. Please restart the ESI Flow from the beginning."
        );
        return;
      }

      if (!currentDocument) {
        setTalkingPointsSummary(
          "Unable to generate talking points: Could not read the current document."
        );
        return;
      }

      console.log("[TalkingPoints] Sending request with document snapshots");

      // Send snapshots as separate fields - backend expects firstDraftSnapshot and currentDraftSnapshot
      const data = await postTalkingPoints({
        firstDraftSnapshot: initialDocument,
        currentDraftSnapshot: currentDocument,
        jurisdiction: jurisdiction || undefined,
        docType: docType || undefined,
      });

      console.log("[TalkingPoints] Response received:", {
        hasAnalysis: !!data?.analysis,
        analysisLength: data?.analysis?.length,
      });

      // Clean up the response
      let summary = (data.analysis || "").trim();
      if (summary === '""' || summary === "''") {
        summary = "";
      }

      // Handle response
      if (!summary || summary === "NO_CHANGES_DETECTED") {
        setTalkingPointsSummary(
          "No material changes were detected between the initial and current document versions."
        );
      } else {
        setTalkingPointsSummary(summary);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error("[TalkingPoints] Error:", errorMessage);
      setTalkingPointsSummary(
        `Unable to generate Talking Points. ${errorMessage}`
      );
    } finally {
      setTalkingPointsLoading(false);
    }
  };

  // Capture baseline document text once, when Jurisdiction Check (analysisComplete) first appears.
  // Uses getFullDocumentText() — getHtml() can block Word for seconds and frequently fails,
  // and the initial snapshot (snapshotAtPluginStart) is already text-based.
  useEffect(() => {
    if (flowStep === "analysisComplete" && !snapshotAtFlowStart) {
      (async () => {
        try {
          const fullText = await getFullDocumentText();
          setSnapshotAtFlowStart(fullText);
        } catch (e) {
          console.error("Failed to capture baseline document snapshot:", e);
        }
      })();
    }
  }, [flowStep, snapshotAtFlowStart]);

  // Copy Icon for Talking Points
  const handleCopyTalkingPoints = async () => {
    let copyText = "";
    if (talkingPointsSummary && talkingPointsSummary.trim().length > 0) {
      copyText = talkingPointsSummary;
    } else {
      acceptedCorrections.forEach((corr) => {
        copyText += `Heading: ${corr.heading}\nDetails: ${corr.details}\n\n`;
      });
    }

    try {
      await navigator.clipboard.writeText(copyText);
      console.log("Talking points copied to clipboard!");
    } catch (err) {
      // Fallback for environments where Clipboard API is blocked
      try {
        const textarea = document.createElement("textarea");
        textarea.value = copyText;
        // Prevent scrolling to bottom in Edge/IE
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        const successful = document.execCommand("copy");
        document.body.removeChild(textarea);
        if (successful) {
          console.log("Talking points copied to clipboard via fallback!");
        } else {
          alert("Unable to copy talking points. Please copy manually.");
        }
      } catch (fallbackErr) {
        console.error("Clipboard fallback failed", fallbackErr);
        alert("Unable to copy talking points. Please copy manually.");
      }
    }
  };

  // Manually refresh Talking Points (re-run LLM summary)
  const handleRefreshTalkingPoints = () => {
    // Avoid double-triggering if already in progress
    if (talkingPointsLoading) return;
    generateTalkingPointsSummary();
  };

  // ============== Clause Check ==============
  const renderClauseCheck = () => {
    const issues = clauseIssues;
    const matchedCourtLink = courtLinks[jurisdiction] || "";

    const courtLinkBox = matchedCourtLink ? (
      <div className="mt-4 rounded-lg bg-gray-50 border border-gray-200 px-4 py-3 text-sm">
        <p className="text-gray-700">
          This court's URL is{" "}
          <a href={matchedCourtLink} target="_blank" rel="noreferrer" className="text-brand-500 underline hover:text-brand-600">
            {matchedCourtLink}
          </a>{" "}
          and should be checked to validate compliance with local rules and standing orders.
        </p>
      </div>
    ) : null;

    return (
      <div>
        {!clauseCheckComplete ? (
          <Spinner label="Scanning clauses, please wait..." />
        ) : clauseRunStatus !== "ok" ? (
          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-3 text-sm text-amber-900">
            <p>
              {clauseRunStatus === "cancelled"
                ? "Clause Check was cancelled because the document may contain hidden instructions to AI. Remove the flagged text, or run the check again to choose how to proceed."
                : "Clause Check couldn't finish, so no results are shown. This is not a clean result — please run it again."}
            </p>
            <button onClick={() => handleClauseCheckAnalysis()} className={`${BTN_PRIMARY_SM} mt-2`}>
              Run Clause Check again
            </button>
          </div>
        ) : issues.length > 0 ? (
          <>
            <p className="text-sm text-gray-700 leading-relaxed">
              {`ESI FLOW has identified ${issues.length} potential topics requiring strategic review in your draft ESI ${docType === 'protocol' ? 'Protocol' : 'Protective Order'}.`}
            </p>
            <hr className="my-3 border-gray-200" />
            {issues.map((issue, index) => {
              const bulletKey = `\u2022 Anchor: ${issue.anchor}`;
              return (
                <IssueCard
                  key={index}
                  index={index}
                  heading={issue.anchor}
                  isExpanded={clauseSelectedBulletIndex === index}
                  onToggle={() => handleClauseMissingRequirementClick(index, bulletKey)}
                  content={clauseSelectedBulletIndex === index ? clauseSelectedBulletContent : ""}
                  onAccept={() => handleClauseCorrectBulletForEditor(index, bulletKey)}
                  onIgnore={() => handleClauseIgnoreBullet(index, bulletKey)}
                />
              );
            })}
            {courtLinkBox}
          </>
        ) : (
          <div>
            <div className="-mx-4 px-4 py-2 mb-2 bg-brand-500 text-white font-semibold text-sm">
              Potential Missing or Incomplete Clauses
            </div>
            <p className="text-sm text-gray-700">All missing clauses have been addressed.</p>
            <button onClick={() => setFlowStep("esiFlowEditor")} className={`${BTN_PRIMARY_SM} w-full mt-3`}>
              Continue to ESI Flow Editor &rarr;
            </button>
            {courtLinkBox}
          </div>
        )}
      </div>
    );
  };

  // ESI Flow Editor
  const renderESIFlowEditor = () => {
    return (
      <div>
        {showTrackChangesWarning && (
          <div className="mb-4 px-4 py-3 bg-yellow-50 border-l-4 border-gold rounded-md">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-gold mr-2" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099C8.578 2.393 9.422 2.393 9.743 3.099l5.904 10.524c.32.706-.17 1.287-.887 1.287H3.24c-.717 0-1.208-.581-.887-1.287L8.257 3.1zM10 12a1 1 0 10-2 0 1 1 0 002 0zm0-3a1 1 0 00-.894.553L9 9v2a1 1 0 002 0V9l.105-.447A1 1 0 0010 9z" clipRule="evenodd"/>
              </svg>
              <span className="text-yellow-700 text-sm">
                Please turn on <strong>Track Changes</strong> before using the ESI Flow Editor.
              </span>
            </div>
          </div>
        )}
        <p className="mb-1 font-semibold text-gray-900">ESI Flow Editor Clipboard</p>
        <p className="mb-3 text-sm text-gray-600 leading-relaxed">Click on the approved recommended language from the Jurisdiction Check and Clause Check sections of ESI Flow to aid in your redlining of this draft.</p>
        {editorFlaws.length > 0 ? (
          editorFlaws.map((flaw, index) => {
            const bulletWithoutSection = flaw.bullet
              .replace(/Section\s+\d+(?:\([a-zA-Z]\))?:?\s*/i, "")
              .replace(/^•\s*/, "")
              .replace(/^\.\d+:\s*/i, "")
              .replace(/Section\s+\d+(\.\d+)?(\([a-z]\))?:\s*/gi, "")
              .replace(/Anchor:\s*/i, "");
            const formattedCorrection = flaw.correction.replace(/Section\s+\d+:\s*/g, "").replace(/\n/g, "<br/>");
            return (
              <IssueCard
                key={index}
                index={index}
                heading={bulletWithoutSection}
                isExpanded={expandedFlawIndex === index}
                onToggle={() => { if (expandedFlawIndex === index) { setExpandedFlawIndex(null); return; } setExpandedFlawIndex(index); handleSuggestionClick(flaw, index); }}
                content={formattedCorrection}
                onAccept={() => handleEditorAccept(flaw, index)}
                onIgnore={() => handleEditorIgnore(index)}
              />
            );
          })
        ) : (
          <div>
            <p className="mb-2 font-semibold text-gray-900">All corrections have been completed.</p>
            <button onClick={() => setFlowStep("talkingPoints")} className={`${BTN_PRIMARY_SM} w-full mt-3`}>
              Continue to Talking Points &rarr;
            </button>
          </div>
        )}
      </div>
    );
  };

  // Top bar with progress
  // …inside App.tsx…

  const renderTopBar = () => {
    const currentIndex = navItems.findIndex(item => item.key === flowStep);
    const jurisdictionCount = jurisdictionIssues.length;
    const editorCount = editorFlaws.length;
    const clauseCount = clauseIssues.length;

    return (
      <div className="flex -mx-4 mb-0 bg-white border-b-2 border-gray-100">
        {navItems.map((item, idx) => {
          const isActive = idx === currentIndex;
          const isPast = idx < currentIndex;
          const iconColor = isActive ? "#ffffff" : isPast ? "#25a55f" : "#9ca3af";

          return (
            <div
              key={item.key}
              className={`flex-1 flex flex-col items-center pt-2.5 pb-2 gap-1 border-b-2 transition-colors duration-200 ${
                isActive
                  ? "border-brand-500 bg-brand-500"
                  : isPast
                  ? "border-brand-300 bg-white"
                  : "border-transparent bg-white"
              }`}
            >
              <div className="relative">
                {item.icon(iconColor)}
                {item.key === "analysisComplete" && jurisdictionRun && (
                  <NavBadge count={jurisdictionCount} variant={jurisdictionCount > 0 ? "warning" : "success"} />
                )}
                {item.key === "clauseCheck" && clauseCheckComplete && clauseRunStatus === "ok" && (
                  <NavBadge count={clauseCount} variant={clauseCount > 0 ? "warning" : "success"} />
                )}
                {item.key === "esiFlowEditor" && editorRun && (
                  <NavBadge count={editorCount} variant={editorCount > 0 ? "warning" : "success"} />
                )}
              </div>
              <span className={`text-[9px] font-semibold leading-tight text-center px-0.5 ${
                isActive ? "text-white" : isPast ? "text-brand-600" : "text-gray-400"
              }`}>
                {item.label}
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  // Main content
  const renderContent = () => {
    switch (flowStep) {
      case "clientChecklist":
        return (
          <div>
            <p className="text-sm text-gray-600 leading-5 mb-4">Select all topics that apply to your client. These inputs help ESI Flow tailor the weighting model and prioritize relevant risks during the Analyze phase.</p>
            <div className="space-y-3">
              {Object.entries(checklistGroups).map(([secId, grp]) => (
                <div key={secId} className="border border-gray-200 rounded-md p-3">
                  <div className="flex items-center justify-between mb-2">
                    <p className="font-semibold text-sm text-gray-900">{grp.label}</p>
                    {boostSections[secId] && <span className="text-xs text-brand-600 font-medium">Boosting +20%</span>}
                  </div>
                  <div className="space-y-2">
                    {grp.items.map((it, idx) => (
                      <label key={idx} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={it.checked}
                          onChange={() => toggleQuestion(secId, idx)}
                          className="accent-brand-500"
                        />
                        <span className="text-sm text-gray-700">{it.q}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <button onClick={() => setFlowStep("analyzing")} className={`${BTN_PRIMARY_SM} w-full mt-4`}>
              Continue to Analyzing &rarr;
            </button>
          </div>
        );
      case "analyzing":
        return (
          <div>
            {analyzeError && <p role="alert" className="mb-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">{analyzeError}</p>}
            {!scorecard && !analyzeLoading && (
              <p className="text-sm text-gray-600 leading-relaxed mb-4">Click &ldquo;Run Draft Analysis&rdquo; to generate your Scorecard. This will evaluate the draft, apply weighting from the Client Checklist, and produce a document-level risk score with sub-indices.</p>
            )}
            {!scorecard && (
              <button onClick={runAnalyzingAI} disabled={analyzeLoading} className={`${BTN_PRIMARY_SM} w-full disabled:opacity-50 disabled:cursor-not-allowed`}>
                {analyzeLoading ? "Analyzing\u2026" : "Run Draft Analysis"}
              </button>
            )}
            {scorecard && (
              <>
                <p className="text-sm text-gray-600 leading-relaxed mb-3">Draft Analysis is complete. The Scorecard below reflects your overall risk score, sub-indices, and key insights. These results do not replace your professional judgment.</p>
                <ScorecardBlock c={scorecard} />
              </>
            )}
          </div>
        );
      case "chooseType":
        return (
          <div>
            <p className="mb-2">
              Thank you for using ESI FLOW. We noticed that you've opened a blank document.
              Would you like help building a draft ESI Protocol or Protective Order?
            </p>
            <div className="space-y-3 mt-3">
              <button onClick={() => handleLoadTemplate("protocol")} className={`${BTN_PRIMARY_LG} w-full`}>
                ESI Protocol Template
              </button>
              <button onClick={() => handleLoadTemplate("protective")} className={`${BTN_PRIMARY_LG} w-full`}>
                Protective Order Template
              </button>
            </div>
          </div>
        );

      case "confirmExisting":
        return (
          <div>
            <p className="mb-2">
              Review the document type before continuing. ESI Flow will use it to select the relevant analysis and guidance.
            </p>
            <label className="block mb-2 text-sm font-semibold" htmlFor="document-type">
              Document type
            </label>
            <select
              id="document-type"
              value={docType || "protocol"}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              onChange={(event) => {
                const type = event.target.value as DocumentType;
                clearPerDocumentState();
                selectedDocTypeRef.current = type;
                docTypeRef.current = type;
                setDocType(type);
              }}
            >
              <option value="protocol">ESI Protocol</option>
              <option value="protective">Protective Order</option>
            </select>
            <p className="mt-2 text-xs text-gray-600">You can correct the detected type here.</p>
            <div className="flex gap-3 mt-3">
              <button onClick={handleConfirmExistingYes} className={`${BTN_PRIMARY_SM}`}>Continue</button>
              <button onClick={handleConfirmExistingNo} className={`${BTN_GHOST}`}>Close</button>
            </div>
          </div>
        );

      case "jurisdictionAuto":
        return (
          <div>
            <p className="mb-2">
              ESI FLOW has analyzed your draft{" "}
              {docType === "protocol" ? "ESI Protocol" : "Protective Order"} and identified
              the jurisdiction as: <strong>{detectedJurisdiction}</strong>.
            </p>
            <p className="mb-2">Is this your matter's jurisdiction?</p>
            {isLoading ? (
              <Spinner label="Preparing Jurisdiction Check, please wait\u2026" />
            ) : (
              <div className="flex gap-3 mt-3">
                <button onClick={handleJurisdictionConfirm} className={`${BTN_PRIMARY_SM}`}>Yes</button>
                <button onClick={handleJurisdictionReject} className={`${BTN_GHOST}`}>No</button>
              </div>
            )}
          </div>
        );

      case "jurisdictionManual":
        return (
          <div>
            <p className="mb-2 font-semibold">Select your jurisdiction:</p>
            <form onSubmit={handleManualJurisdictionSubmit}>
              <select name="jurisdiction" className="w-full border border-gray-300 rounded-md px-3 py-2 mb-4 text-sm focus:ring-2 focus:ring-brand-300 focus:border-brand-500 outline-none">
                {(docStatus === "existing" ? manualJurisdictions : TEMPLATE_JURISDICTIONS).map((option, idx) => (
                  <option key={idx} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              <br />
              <button type="submit" className={`${BTN_PRIMARY_SM} w-full mt-1`}>
                Submit
              </button>
            </form>
          </div>
        );

      case "nextSteps":
        return (
          <div>
            <p className="mb-2">
              {docType === "protocol"
                ? `Proceeding with ESI Protocol drafting flow. Jurisdiction set to: ${jurisdiction}. Click below to begin the Jurisdiction Check.`
                : `Proceeding with Protective Order drafting flow. Jurisdiction set to: ${jurisdiction}. Click below to begin the Jurisdiction Check.`}
            </p>
            {isLoading ? (
              <Spinner label="Preparing Jurisdiction Check, please wait\u2026" />
            ) : (
              <button onClick={handleFlawAnalysis} className={`${BTN_PRIMARY_SM} w-full mt-3`}>
                Analyze Draft for Jurisdiction Requirements
              </button>
            )}
          </div>
        );

      // ============== Jurisdiction Check ==============
      case "analysisComplete": {
        return renderJurisdictionAnalysis();
      }

      // ============== Clause Check ==============
      case "clauseCheck":
        return renderClauseCheck();

      // ============== ESI Flow Editor ==============
      case "esiFlowEditor":
        return renderESIFlowEditor();

      // ============== Talking Points ==============
      case "talkingPoints":
        return (
          <div>
            <div className="mb-1 flex items-center justify-between">
              <p className="font-semibold">Talking Points</p>
              <button
                type="button"
                onClick={handleRefreshTalkingPoints}
                disabled={talkingPointsLoading}
                className={`${BTN_GHOST} inline-flex items-center disabled:cursor-not-allowed disabled:opacity-60`}
                title="Regenerate Talking Points from the latest document"
              >
                ↻ Refresh
              </button>
            </div>
            <p className="mb-3 text-sm text-gray-600 leading-5">
              This section summarizes the edits made in this ESI Protocol for internal preparatory purposes,
              including your negotiation positions and strategic fallback options.
            </p>
            {talkingPointsLoading ? (
              <Spinner label="Generating Talking Points, please wait..." />
            ) : !talkingPointsSummary ? (
              <Spinner label="Generating Talking Points, please wait..." />
            ) : (() => {
              const noChangesMessage =
                "No changes were made in the document between the start of the Jurisdiction Check and the current version.";

              if (talkingPointsSummary.startsWith(noChangesMessage)) {
                return (
                  <div className="mt-2 rounded-md border border-brand-200 bg-brand-50 px-3 py-2 text-sm text-brand-700">
                    {noChangesMessage}
                  </div>
                );
              }

              // Format the summary so key labels are bolded while preserving simple layout
              const blocks = talkingPointsSummary
                .split(/\n\s*\n/)
                .map((b) => b.trim())
                .filter((b) => b.length > 0);

              const renderLine = (line: string, idx: number) => {
                const match = line.match(
                  /^(Topic|Original Language|Actual Edits|Recommended Edits|RECOMMENDED Edits|Position|Strategic Fallback):\s*(.*)$/i
                );
                if (!match) {
                  return (
                    <p key={idx} className="mb-1">
                      {line}
                    </p>
                  );
                }
                let label = match[1];
                // Normalize edit labels for display
                if (label.toLowerCase() === "actual edits" || label.toLowerCase() === "recommended edits") {
                  label = "Actual Edits";
                }
                const rest = match[2];
                return (
                  <p key={idx} className="mb-1">
                    <span className="font-semibold">{label}:</span>{" "}
                    {rest}
                  </p>
                );
              };

              return (
                <div className="mt-2">
                  <div className="space-y-3">
                    {blocks.map((block, bIdx) => {
                      const lines = block
                        .split(/\r?\n/)
                        .map((l) => l.trim())
                        .filter((l) => l.length > 0);
                      return (
                        <div
                          key={bIdx}
                          className="rounded-md border border-brand-100 bg-brand-50 px-3 py-2 text-sm"
                        >
                          {lines.map((line, lIdx) => renderLine(line, lIdx))}
                        </div>
                      );
                    })}
                  </div>
                  <button
                    onClick={handleCopyTalkingPoints}
                    className="inline-flex items-center mt-3 text-sm text-brand-500 hover:text-brand-600"
                    title="Copy Talking Points"
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                    </svg>
                    <span className="ml-1">Copy</span>
                  </button>
                </div>
              );
            })()}
          </div>
        );

      case "closed":
        return (
          <div>
            <p className="mb-2">Thank you for using ESI FLOW.</p>
          </div>
        );

      default:
        return <p>Welcome to ESI FLOW. Scanning document content...</p>;
    }
  };

  // Jurisdiction Analysis Complete rendering
  const renderJurisdictionAnalysis = () => {
    if (analysis.startsWith("Error:")) {
      return (
        <div role="alert" className="rounded-md border border-amber-200 bg-amber-50 px-3 py-3 text-sm text-amber-900">
          <p>Jurisdiction Check couldn't finish. {analysis.replace(/^Error:\s*/, "")}</p>
          <button onClick={handleFlawAnalysis} disabled={isLoading} className={`${BTN_PRIMARY_SM} mt-2`}>
            Run Jurisdiction Check again
          </button>
          <button onClick={() => setFlowStep("clauseCheck")} className={`${BTN_GHOST} mt-2`}>
            Continue to Clause Check
          </button>
        </div>
      );
    }
    if (analysis === "__LOADING__") {
      return (
        <div className="flex flex-col items-center justify-center py-8">
          <Spinner size="lg" label="Analyzing document for jurisdiction requirements..." />
        </div>
      );
    }

    const issues = jurisdictionIssues;
    const missingCount = issues.length;
    const matchedCourtLink = courtLinks[jurisdiction] || "";

    return (
      <div>
        {/* District Court ESI Summary - collapsible card */}
        <div className="mb-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
          <div
            className="flex items-center gap-2 cursor-pointer font-semibold text-sm text-gray-800"
            onClick={() => setJurisdictionSummaryExpanded((prev) => !prev)}
          >
            <span className="text-xs text-gray-400">{jurisdictionSummaryExpanded ? "\u25BC" : "\u25B6"}</span>
            <span>District Court ESI Summary</span>
          </div>
          {jurisdictionSummaryExpanded && (
            <div className="mt-2 text-sm text-gray-700" style={{ whiteSpace: "pre-wrap" }}>
              {jurisdictionSummaryLoading ? (
                <span className="text-gray-400">Loading summary...</span>
              ) : jurisdictionSummary && jurisdictionSummary.trim().length > 0 ? (
                jurisdictionSummary
              ) : (
                <span className="text-gray-400">Summary will appear here.</span>
              )}
            </div>
          )}
        </div>

        {/* Status message */}
        <p className="text-sm text-gray-700 leading-relaxed">
          {missingCount > 0
            ? `ESI Flow has identified ${missingCount} potential topic${missingCount === 1 ? "" : "s"} for jurisdiction review in your draft ${docType === "protective" ? "Protective Order" : "ESI Protocol"}.`
            : "No additional issues were identified among the supplied jurisdiction requirements."}
        </p>
        <hr className="my-3 border-gray-200" />

        {/* Pre-fetch status indicator */}
        {prefetchingJurisdiction && issues.length > 0 && (
          <div className="mb-2">
            <Spinner size="sm" label="Loading details for all issues..." />
          </div>
        )}

        {issues.map((issue, index) => {
          const bulletKey = `\u2022 Anchor: ${issue.anchor}`;
          return (
            <div key={index}>
              {issue.ruleReference && (
                <p className="mb-1 text-xs text-gray-600">
                  Source: {issue.ruleReference}{issue.sourceType ? ` · ${issue.sourceType}` : ""}
                </p>
              )}
            <IssueCard
              index={index}
              heading={issue.anchor}
              isExpanded={selectedBulletIndex === index}
              onToggle={() => handleJurisdictionFlawClick(index, bulletKey)}
              content={selectedBulletIndex === index ? selectedBulletContent : ""}
              onAccept={() => handleCorrectBulletForEditor(index, bulletKey)}
              onIgnore={() => handleIgnoreBulletForEditor(index, bulletKey)}
            />
            </div>
          );
        })}

        {missingCount === 0 && (
          <div className="mt-4">
            <button onClick={() => setFlowStep("clauseCheck")} className={`${BTN_PRIMARY_SM} w-full mt-3`}>
              Continue to Clause Check &rarr;
            </button>
          </div>
        )}

        {matchedCourtLink && (
          <div className="mt-4 rounded-lg bg-gray-50 border border-gray-200 px-4 py-3 text-sm">
            <p className="text-gray-700">
              This court's URL is{" "}
              <a href={matchedCourtLink} target="_blank" rel="noreferrer" className="text-brand-500 underline hover:text-brand-600">
                {matchedCourtLink}
              </a>{" "}
              and should be checked to validate compliance with local rules and standing orders.
            </p>
          </div>
        )}
      </div>
    );
  };

  // Helper to get full document HTML (preserves formatting such as strike-through)
  // PERF: For large documents (>50k chars), getHtml() can take 5-10s and generate
  // 20-50MB of HTML. Fall back to plain text for documents above the threshold.
  const getFullDocumentHtml = async () => {
    return await queuedWordRun(async (context) => {
      const body = context.document.body;
      body.load("text");
      await context.sync();

      // For large documents, skip HTML serialization to avoid freezing
      if (body.text.length > 50000) {
        console.warn("[PERF] Document too large for HTML snapshot (" + body.text.length + " chars), using text");
        return body.text;
      }

      try {
        const htmlResult = body.getHtml();
        await context.sync();
        const html = htmlResult.value || "";

        // Guard: getHtml() can return an empty HTML skeleton (e.g. <HTML><HEAD></HEAD><BODY></BODY></HTML>)
        // when the document context is stale or the document lacks editing focus.
        // Fall back to body.text (already loaded above) which is more reliable.
        const strippedText = html.replace(/<[^>]*>/g, "").trim();
        if (html.length > 0 && strippedText.length === 0 && body.text.trim().length > 0) {
          console.warn("[PERF] getHtml() returned empty HTML skeleton, falling back to text");
          return body.text;
        }

        return html;
      } catch (htmlError) {
        // getHtml() can fail with tracked changes or complex formatting
        console.warn("[PERF] getHtml() failed, using text fallback:", htmlError);
        return body.text;
      }
    });
  };

  // Helper to get full document text
  // PERF: Uses body.load("text") instead of loading all paragraph objects.
  // body.text is a single string read vs creating 500+ JS objects for large docs.
  const getFullDocumentText = async () => {
    return await queuedWordRun(async (context) => {
      const body = context.document.body;
      body.load("text");
      await context.sync();
      return body.text;
    });
  };

  async function runAnalyzingAI() {
    setAnalyzeLoading(true);
    setAnalyzeError("");
    setScorecard(null);
    try {
      const docContent = await guardDocument(await getFullDocumentText());
      if (docContent === null) return; // user cancelled at the injection check
      const weightBoost = Object.fromEntries(Object.entries(boostSections).filter(([,v]) => !!v));
      // Collect all checked questions by section for the AI prompt context
      const selectedQuestions = Object.entries(checklistGroups).flatMap(([secId, grp]) =>
        (grp.items || []).filter(it => it.checked).map(it => ({ sectionId: secId, question: it.q }))
      );
      const card: Scorecard = await postScorecard({
        docContent,
        jurisdiction: jurisdiction || "Unknown",
        docType: (docType === "protocol" || docType === "protective") ? docType : "protocol",
        weightBoost,
        selectedQuestions,
      });
      setScorecard(card);
      // Capture the first scorecard from the main Analyzing stage as the baseline
      setBaselineScorecard((prev) => {
        if (prev) return prev;
        return flowStep === "analyzing" ? card : prev;
      });
    } catch (e) {
      console.error("AI analyzing failed:", e);
      setAnalyzeError("Draft Analysis couldn't finish. No scorecard is available for this run. Please try Run Draft Analysis again.");
    } finally {
      setAnalyzeLoading(false);
    }
  }

  // Add a ref to always have the latest docType
  const docTypeRef = useRef<DocType | null>(null);
  useEffect(() => {
    docTypeRef.current = docType;
  }, [docType]);


  // look up the current stage label (only the 4 main steps are in navItems)
  const currentStageLabel = navItems.find(item => item.key === flowStep)?.label;

  // Capture document text when entering ESI Flow Editor stage
  // PERF: Get both snapshot and text for scorecard in a single Word.run call
  // to avoid two sequential document reads (was 8-15s, now 1-2s).
  useEffect(() => {
    if (flowStep === "esiFlowEditor") {
      setEditorRun(true);
      (async () => {
        // Single Word.run to get both snapshot and text for scorecard
        // Use text only — getHtml() can block Word for seconds and frequently fails.
        // All talking-points snapshots are now text-based for consistency.
        const { snapshot, text } = await queuedWordRun(async (context) => {
          const body = context.document.body;
          body.load("text");
          await context.sync();
          const docText = body.text;
          return { snapshot: docText, text: docText };
        });

        setSnapshotBeforeEditor(snapshot);

        // Run scorecard with the text we already have (no second Word.run needed)
        setAnalyzeLoading(true);
        try {
          const weightBoost = Object.fromEntries(Object.entries(boostSections).filter(([,v]) => !!v));
          const selectedQuestions = Object.entries(checklistGroups).flatMap(([secId, grp]) =>
            (grp.items || []).filter(it => it.checked).map(it => ({ sectionId: secId, question: it.q }))
          );
          const card: Scorecard = await postScorecard({
            docContent: applyInjectionDecision(text),
            jurisdiction: jurisdiction || "Unknown",
            docType: (docType === "protocol" || docType === "protective") ? docType : "protocol",
            weightBoost,
            selectedQuestions,
          });
          setScorecard(card);
          // Don't set baseline here - this is the esiFlowEditor refresh,
          // baseline is only set from the initial "analyzing" step via runAnalyzingAI
        } catch (e) {
          console.error("AI analyzing failed:", e);
          alert("Analyzing failed. See console for details.");
        } finally {
          setAnalyzeLoading(false);
        }
      })();
    }
  }, [flowStep]);

  // Automatically generate Talking Points when entering the Talking Points step
  useEffect(() => {
    if (flowStep === "talkingPoints" && !talkingPointsLoading && !talkingPointsSummary) {
      generateTalkingPointsSummary();
    }
  }, [flowStep, talkingPointsLoading, talkingPointsSummary]);

  // Show Track Changes warning on entering ESI Flow Editor and hide after 10 seconds
  useEffect(() => {
    let timer: NodeJS.Timeout | null = null;
    if (flowStep === "esiFlowEditor") {
      setShowTrackChangesWarning(true);
      timer = setTimeout(() => setShowTrackChangesWarning(false), 10000);
    }
    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [flowStep]);

  // Load Client Checklist questions when entering the panel
  useEffect(() => {
    const loadChecklist = async () => {
      if (flowStep !== "clientChecklist") return;
      const typ = (docType === "protocol" || docType === "protective") ? docType : "protocol";
      const data = await getClientChecklist(typ as "protocol" | "protective");
      const groups: Record<string, {label:string, items:{q:string, checked:boolean}[]}> = {};
      const labels = (data.sectionLabels || {}) as Record<string,string>;
      (data.questions || []).forEach((row: any) => {
        const sec = row.sectionId;
        if (!groups[sec]) groups[sec] = { label: labels[sec] || sec, items: [] };
        groups[sec].items.push({ q: row.question, checked: false });
      });
      setChecklistQuestions(data.questions || []);
      setChecklistGroups(groups);
      setBoostSections({});
    };
    loadChecklist();
  }, [flowStep, docType]);

  // Toggle a checklist item and recompute per-section boost
  const toggleQuestion = (sectionId: string, idx: number) => {
    setChecklistGroups(prev => {
      const copy = { ...prev } as typeof prev;
      if (!copy[sectionId]) return prev;
      copy[sectionId] = { ...copy[sectionId] };
      copy[sectionId].items = copy[sectionId].items.map((it, i) => i === idx ? { ...it, checked: !it.checked } : it);
      const anyChecked = copy[sectionId].items.some(it => it.checked);
      setBoostSections(prevBoost => ({ ...prevBoost, [sectionId]: anyChecked }));
      return copy;
    });
  };

  return (
    <div className="p-4 font-sans text-gray-900 min-h-screen bg-white">
      {renderTopBar()}

      {injectionScanning && !injectionReview && (
        <div role="status" className="mb-3 rounded-md border border-brand-200 bg-brand-50 px-3 py-2 text-xs text-brand-700">
          Checking the document for hidden instructions to AI…
        </div>
      )}
      {injectionNotice && !injectionScanning && (
        <div role="status" className="mb-3 flex items-start justify-between gap-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          <span>{injectionNotice}</span>
          <button onClick={() => setInjectionNotice("")} className="shrink-0 font-medium underline">
            Dismiss
          </button>
        </div>
      )}
      {injectionReview && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-3"
          role="dialog"
          aria-modal="true"
          aria-labelledby="injection-title"
        >
          <div className="w-full max-w-md rounded-lg bg-white p-4 shadow-xl">
            <h2 id="injection-title" className="text-base font-semibold text-gray-900">
              Possible hidden instructions to AI
            </h2>
            <p className="mt-1 text-xs text-gray-600">
              This document contains text that looks written to steer an AI review rather than to form part of the
              agreement. It may be hidden — for example white or very small text. Review it before analyzing.
            </p>
            <ul className="mt-3 max-h-72 space-y-2 overflow-y-auto">
              {injectionReview.findings.map((f, i) => (
                <li key={i} className="rounded-md border border-red-200 bg-red-50 p-2">
                  <p className="break-words text-xs italic text-gray-900">
                    &ldquo;{f.quote.length > 300 ? `${f.quote.slice(0, 300)}…` : f.quote}&rdquo;
                  </p>
                  <p className="mt-1 text-xs text-gray-700">{f.reason}</p>
                  <button
                    onClick={() => showInjectionInDocument(f.quote)}
                    className="mt-1 text-xs font-medium text-brand-700 underline"
                  >
                    Show in document
                  </button>
                </li>
              ))}
            </ul>
            {injectionReview.partial && (
              <p className="mt-2 text-xs text-amber-800">Part of the document couldn&apos;t be checked.</p>
            )}
            <div className="mt-4 flex flex-col gap-2">
              <button className={`${BTN_PRIMARY_SM} w-full`} onClick={() => injectionReview.resolve("exclude")}>
                Exclude flagged text &amp; analyze
              </button>
              <button className={`${BTN_SECONDARY_SM} w-full`} onClick={() => injectionReview.resolve("anyway")}>
                Analyze anyway
              </button>
              <button className={`${BTN_GHOST} w-full`} onClick={() => injectionReview.resolve("cancel")}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Combined stage header: Back (left) — heading (true centre) — action (right) */}
      {currentStageLabel && flowStep !== "chooseType" && flowStep !== "confirmExisting" && (
        <div className="-mx-4 px-4 py-2.5 mb-4 border-b border-gray-200 bg-white flex items-center gap-2">
          {/* Each of the three slots takes equal flex space, so the centre slot
              stays geometrically centred regardless of button widths on the sides. */}
          <div className="flex-1 flex items-center min-w-0">
            <button onClick={handleBack} className={BTN_GHOST}>
              &larr; Back
            </button>
          </div>
          <div className="flex-1 flex items-center justify-center min-w-0">
            <h2 className="flex items-center gap-1.5 text-sm font-semibold text-gray-900 truncate">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-brand-500 flex-shrink-0" />
              <span className="truncate">{currentStageLabel}</span>
            </h2>
          </div>
          <div className="flex-1 flex items-center justify-end gap-1.5 min-w-0">
            {flowStep === "analyzing" && (
              <button
                onClick={() => {
                  if (docStatus === "existing") {
                    setFlowStep("jurisdictionAuto");
                  } else {
                    setFlowStep("nextSteps");
                  }
                }}
                className={BTN_SECONDARY_SM}
              >
                Continue &rarr;
              </button>
            )}
            {flowStep === "analysisComplete" && jurisdictionIssues.length > 0 && (
              <button onClick={() => setFlowStep("clauseCheck")} className={BTN_SECONDARY_SM}>
                Skip &rarr;
              </button>
            )}
            {flowStep === "clauseCheck" && (
              <button onClick={() => setFlowStep("esiFlowEditor")} className={BTN_SECONDARY_SM}>
                Skip &rarr;
              </button>
            )}
          </div>
        </div>
      )}

      {/* Upload widget only on the entry flows */}
      {(flowStep === "chooseType" || flowStep === "confirmExisting") && (
        <div className="mb-4">
          <CustomDataLayerUpload className="w-full mt-4" />
        </div>
      )}

      {renderContent()}
    </div>
  );

};

export default App;
